import React, { useState, useEffect, useRef, useCallback } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Avatar, 
  Menu, 
  MenuItem, 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid, 
  Card, 
  CardMedia, 
  CardContent,
  Tabs,
  Tab,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Chip,
  CircularProgress,
  Select,
  FormControl
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import { PREDEFINED_AVATARS } from '../../constants/avatars';
import api from '../../services/api';
import axios from 'axios';
import DocumentPanel from '../document/DocumentPanel';
import CloseIcon from '@mui/icons-material/Close';
import SplitPane from './SplitPane';
import CategoryTree from '../category/CategoryTree';
import CategoryContent from '../category/CategoryContent';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import { Document } from '../../types/Document';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ContentEditor from '../content/ContentEditor';
import { fetchCategoryWithChildren } from '../../services/CategoryService';
import { updateCategoryInTree } from '../../services/CategoryService';
import { Content } from '../../types/Content';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import { historyService } from '../../services/HistoryService';
import { Logout } from '@mui/icons-material';
import { Outlet } from 'react-router-dom';
import { useDocument } from '../../contexts/DocumentContext';
import UploadIcon from '@mui/icons-material/Upload';
import { UserDetailsDto } from '../../types/UserDetailsDto';
import SettingsMenu from '../SettingsMenu/SettingsMenu';
import SemanticSearchWindow from '../search/SemanticSearchWindow';
import SearchIcon from '@mui/icons-material/Search';
import { SearchSettingsProvider } from '../../contexts/SearchSettingsContext';
import AiSparklesIcon from '../icons/AiSparklesIcon';
import { MergeMissingResolve, MergeTypeMismatchResolve } from '../../types/MergeTypes';
import { CustomDragLayer } from '../category/CustomDragLayer';
import RenameSubcaseDialog from '../dialogs/RenameSubcaseDialog';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import JoinFullIcon from '@mui/icons-material/JoinFull';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HelpIcon from '@mui/icons-material/Help';
import SubTreeView from './SubTreeView';
import ExpirationWarning from '../auth/ExpirationWarning';
import AdminConsolePage from '../../pages/AdminConsolePage';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import UserProfileDialog from '../dialogs/UserProfileDialog';
import { SubcaseGroupsDialog } from '../dialogs/SubcaseGroupsDialog';
import HelpDialog from '../dialogs/HelpDialog';
import GroupIcon from '@mui/icons-material/Group';
import { Category } from '../../types/Category';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import FindCategoryDialog from '../dialogs/FindCategoryDialog';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import DuplicateCategoryAsDocumentDialog from '../dialogs/DuplicateCategoryAsDocumentDialog';
import GenerateSubtreeDialog from '../dialogs/GenerateSubtreeDialog';
import { useHelpContent } from '../../utils/HelpUtils';
import { hasDuplicateContent, getDuplicateContentMessage, getContentIndex } from '../../utils/CategoryUtils';


const Root = styled('div')({
  display: 'flex',
  height: '100vh',
  '& .MuiToolbar-root': {
    paddingLeft: '24px', // Match the content pane padding
    paddingRight: '24px',
  },
});

const StyledAppBar = styled(MuiAppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 2,
  backgroundColor: '#2c3e50',
  '& .MuiToolbar-root': {
    paddingLeft: '24px', // Match the content pane padding
    paddingRight: '24px',
  },
}));

// Prevent scrolling on the main pane
const MainContent = styled('main')({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  marginTop: '64px',
  backgroundColor: '#f5f6fa',
  overflow: 'hidden',
});

// Prevent scrolling on the container
const SplitPaneContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  height: '100%',
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
});

const Pane = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
});

const Resizer = styled('div')({
  width: '4px',
  backgroundColor: '#9e9e9e',
  cursor: 'col-resize',
  position: 'relative',
  userSelect: 'none',
  flexShrink: 0,
  '&:hover': {
    backgroundColor: '#757575',
  },
});

const TabPanel = styled('div')({
  height: '100%',
  display: 'none',
  '&.active': {
    display: 'flex',
    flexDirection: 'column',
  },
});

const StyledTabs = styled(Tabs)({
  backgroundColor: '#ffffff',
  borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
  '& .MuiTab-root': {
    textTransform: 'none',
    minWidth: 'auto',
    padding: '0 16px',
  },
});

const TabLabel = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const CloseButton = styled('div')({
  padding: '4px',
  marginLeft: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
});

const LogoImage = styled('img')({
  height: '56px',
  marginRight: '24px',
});

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: 'inherit',
  padding: theme.spacing(0.75),
  minWidth: 'auto',
  '&.Mui-disabled': {
    color: 'rgba(255, 255, 255, 0.5)', // More visible gray color for disabled state
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
  },
  marginRight: theme.spacing(0.25),
}));

const ContentPanel = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  transition: theme.transitions.create(['height'], {
    duration: theme.transitions.duration.standard,
  }),
}));

const ContentPanelHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  gap: theme.spacing(1),
}));

const ContentPanelTitle = styled(Box)(({ theme }) => ({
  flex: 1,
  fontWeight: 500,
}));

const ContentPanelContent = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  overflow: 'auto',
}));

const ResizeHandle = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '4px',
  backgroundColor: theme.palette.divider,
  cursor: 'row-resize',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  color: 'white',
  '& .MuiSelect-select': {
    color: 'white',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.23)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.5)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'white',
  },
  '& .MuiSvgIcon-root': {
    color: 'white',
  },
  minWidth: 200,
  marginRight: theme.spacing(0.5),
}));

interface Tab {
  id: string;
  label: string;
  content: React.ReactNode;
}

const APP_BAR_HEIGHT = 64; // px
const WARNING_HEIGHT = 48; // px

interface MainLayoutProps {
  showWarning: boolean;
}

const MainLayout: React.FC<MainLayoutProps> = ({ showWarning }) => {
  const { logout, user, fetchUserDetails } = useAuth();
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [currentTab, setCurrentTab] = useState<string | null>(null);
  const [currentDocument, setCurrentDocument] = useState<Document | undefined>();
  const [splitPosition, setSplitPosition] = useState(30); // percentage
  const resizerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [clipboardCategory, setClipboardCategory] = useState<Category | null>(null);
  const [clipboardContents, setClipboardCContents] = useState<Content[]>([]);
  const [isCutOperation, setIsCutOperation] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [addError, setAddError] = useState('');
  const [renameError, setRenameError] = useState('');
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const [documentExpandedStates, setDocumentExpandedStates] = useState<{ [documentId: string]: string[] }>({});
  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem('fontSize');
    return saved ? parseInt(saved) : 14;
  });

  // Initialize global font size on component mount
  useEffect(() => {
    document.documentElement.style.setProperty('--app-font-size', `${fontSize}px`);
  }, [fontSize]);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [splitNames, setSplitNames] = useState({ first: '', second: '' });
  const [splitError, setSplitError] = useState('');
  const [addCategoryDialogOpen, setAddCategoryDialogOpen] = useState(false);
  const [groupCategoriesDialogOpen, setGroupCategoriesDialogOpen] = useState(false);
  const [mergeCategoriesDialogOpen, setMergeCategoriesDialogOpen] = useState(false);
  const [generateSubtreeDialogOpen, setGenerateSubtreeDialogOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [selectedContents, setSelectedContents] = useState<Content[]>([]);
  const [isContentPanelOpen, setIsContentPanelOpen] = useState(true);
  const [contentPanelHeight, setContentPanelHeight] = useState(300);
  const [isEditingContent, setIsEditingContent] = useState(false);
  const contentPanelRef = useRef<HTMLDivElement>(null);
  const resizeStartY = useRef<number>(0);
  const resizeStartHeight = useRef<number>(0);
  const [rightPaneSplitPosition, setRightPaneSplitPosition] = useState(40); // percentage
  const rightPaneResizerRef = useRef<HTMLDivElement>(null);
  const isRightPaneDraggingRef = useRef(false);
  const [subcases, setSubcases] = useState<string[]>([]);
  const [currentContent, setCurrentContent] = useState<{ subcase: string; text: string } | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [avatarMenuAnchorEl, setAvatarMenuAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const avatarMenuOpen = Boolean(avatarMenuAnchorEl);
  const { documents, addDocument, createDocument, updateDocument, deleteDocument, refreshDocuments, duplicateDocument, fetchSubcases, mergeDocument } = useDocument();
  const [avatarDataUrl, setAvatarDataUrl] = useState<string | null>(null);
  const [predefinedAvatarDataUrls, setPredefinedAvatarDataUrls] = useState<{[key: string]: string}>({});
  const [loading, setLoading] = useState(false);
  const [autoPasteEnabled, setAutoPasteEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('autoPasteEnabled');
    return saved ? JSON.parse(saved) : true;
  });
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isDocumentPanelVisible, setIsDocumentPanelVisible] = useState(true);
  const [openRenameSubcaseDialog, setOpenRenameSubcaseDialog] = useState(false);
  const [duplicateCategoryDialogOpen, setDuplicateCategoryDialogOpen] = useState(false);
  // Add state for delete confirmation dialogs
  const [openDeleteCategoryDialog, setOpenDeleteCategoryDialog] = useState(false);
  const [openDeleteContentDialog, setOpenDeleteContentDialog] = useState(false);
  const [showSubTreeDialogOpen, setShowSubTreeDialogOpen] = useState(false);
  const [includeDescriptions, setIncludeDescriptions] = useState(true);
  const [includeContent, setIncludeContent] = useState(true);
  const [selectedSubcases, setSelectedSubcases] = useState<string[]>([]);
  const [subTreeOverlayOpen, setSubTreeOverlayOpen] = useState(false);
  const [subTreeOverlayData, setSubTreeOverlayData] = useState<{ category: Category | null, subcases: string[], includeDescriptions: boolean }>({ category: null, subcases: [], includeDescriptions: true });
  const [subTreeSearch, setSubTreeSearch] = useState('');
  const [subTreeCaseSensitive, setSubTreeCaseSensitive] = useState(false);
  const [subTreeWholeWord, setSubTreeWholeWord] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const navigate = useNavigate();
  const location = useLocation();
  const [subcaseGroupsDialogOpen, setSubcaseGroupsDialogOpen] = useState(false);
  const [subcaseGroups, setSubcaseGroups] = useState<{ name: string; subcases: string[] }[]>([]);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [loadingSubcaseGroups, setLoadingSubcaseGroups] = useState(false);
  const [selectedSubcaseGroup, setSelectedSubcaseGroup] = useState<string | null>(null);
  const [selectedSubcase, setSelectedSubcase] = useState<string>('');
  // Category find feature state
  const [findDialogOpen, setFindDialogOpen] = useState(false);
  const [findSearchString, setFindSearchString] = useState('');
  const [findResults, setFindResults] = useState<Category[]>([]);
  const [currentFindIndex, setCurrentFindIndex] = useState(0);
  const [findStarted, setFindStarted] = useState(false);
  const [showEndWarning, setShowEndWarning] = useState(false);
  const [showStartWarning, setShowStartWarning] = useState(false);
  const [hasWrappedAround, setHasWrappedAround] = useState(false);
  const [highlightedText, setHighlightedText] = useState<string>('');
  const [searchTerms, setSearchTerms] = useState<string[]>([]);
  const [searchMode, setSearchMode] = useState<'any' | 'all' | 'exact' | 'semantic'>('exact');
  const [wholeWord, setWholeWord] = useState<boolean>(false);
  const [caseSensitive, setCaseSensitive] = useState<boolean>(false);
  // Reactive variable for the current document (document in active tab)
  const [activeDocument, setActiveDocument] = useState<Document | undefined>();
  const [matchingContentIds, setMatchingContentIds] = useState<string[]>([]);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [toolbarMenuAnchorEl, setToolbarMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [toolbarRef, setToolbarRef] = useState<HTMLDivElement | null>(null);
  const [visibleButtons, setVisibleButtons] = useState<Set<string>>(new Set(['add', 'edit', 'delete']));

  // Dialog position and drag state
  const [dialogPosition, setDialogPosition] = useState({ x: window.innerWidth - 350, y: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Ref for scrolling to matched categories
  const categoryTreeRef = useRef<HTMLDivElement>(null);

  // Content pane ref for auto-scroll
  const contentPaneRef = useRef<HTMLDivElement>(null);
  const categoryContentRef = useRef<HTMLDivElement>(null);

  const { getToolbarTooltipTextForState } = useHelpContent();

  // Add state for unsaved changes confirmation dialog
  const [unsavedChangesDialog, setUnsavedChangesDialog] = useState<{
    open: boolean;
    action: (() => void) | null;
    message: string;
  }>({
    open: false,
    action: null,
    message: ''
  });

  // Add this near other state declarations
  const [showEditorWarning, setShowEditorWarning] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAvatarMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAvatarMenuAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAvatarMenuClose = () => {
    setAvatarMenuAnchorEl(null);
  };

  const handleToolbarMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setToolbarMenuAnchorEl(event.currentTarget);
  };

  const handleToolbarMenuClose = () => {
    setToolbarMenuAnchorEl(null);
  };

  const handleLogout = async () => {
    historyService.clear();
    handleClose();
    await logout();
  };

  // Update undo/redo state
  useEffect(() => {
    const interval = setInterval(() => {
      setCanUndo(historyService.canUndo());
      setCanRedo(historyService.canRedo());
    }, 100);
    return () => clearInterval(interval);
  }, []);

  // Handle window resize and calculate visible buttons
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate which buttons should be visible based on available space
  useEffect(() => {
    if (!toolbarRef) return;

    const calculateVisibleButtons = () => {
      const toolbarWidth = toolbarRef.offsetWidth;
      const buttonWidth = 32; // Even more compact button width (including padding)
      const spacing = 1; // Very tight gap between buttons
      const logoWidth = 100; // More accurate logo width
      const userMenuWidth = 150; // More accurate user menu width
      const subcaseWidth = activeDocument?.subcases ? 180 : 0;
      const searchWidth = 80; // More accurate search/AI buttons width
      const moreButtonWidth = 40; // Width for "More" button if needed

      // Calculate available space for buttons with minimal buffer
      const reservedSpace = logoWidth + userMenuWidth + subcaseWidth + searchWidth + 5; // Very minimal buffer
      const availableSpace = toolbarWidth - reservedSpace;

      // Calculate how many buttons can fit
      let maxButtons = Math.floor(availableSpace / (buttonWidth + spacing));

      // Be more aggressive with space usage for all screen sizes
      if (toolbarWidth > 1400) {
        maxButtons = Math.floor(availableSpace / (buttonWidth + 1)); // Very tight spacing
      } else if (toolbarWidth > 1000) {
        maxButtons = Math.floor(availableSpace / (buttonWidth + 1.5)); // Tighter spacing
      } else {
        maxButtons = Math.floor(availableSpace / (buttonWidth + 2)); // Standard tight spacing
      }

      // Define button priority order (most important first)
      const buttonPriority = [
        'add', 'edit', 'delete', // Primary actions
        'split', 'group', 'flatten', 'merge', // Category operations
        'generate', 'rename', // More category operations
        'moveUp', 'moveDown', // Movement
        'cut', 'copy', 'paste', // Clipboard
        'undo', 'redo', // History
        'duplicate', 'renameSubcase', 'subcaseGroups' // Document operations
      ];

      const newVisibleButtons = new Set<string>();

      // Show as many buttons as possible, but at least the primary actions
      const buttonsToShow = Math.max(3, Math.min(maxButtons, buttonPriority.length));

      for (let i = 0; i < buttonsToShow; i++) {
        newVisibleButtons.add(buttonPriority[i]);
      }

      setVisibleButtons(newVisibleButtons);
    };

    // Calculate on mount and when dependencies change
    calculateVisibleButtons();

    // Recalculate when window resizes
    const resizeObserver = new ResizeObserver(calculateVisibleButtons);
    if (toolbarRef) {
      resizeObserver.observe(toolbarRef);
    }

    return () => resizeObserver.disconnect();
  }, [toolbarRef, windowWidth, activeDocument?.subcases]);
  // Keep activeDocument in sync with current tab
  useEffect(() => {
    const currentDoc = getActiveDocument();
    setActiveDocument(currentDoc);
  }, [currentTab, documents]);

  const findCategoryInTree = (categories: Category[], targetId: string): Category | null => {
    // First check root level
    const category = categories.find(cat => cat.id === targetId);
    if (category) return category;

    // Then check each category's children
    for (const parent of categories) {
      if (parent.children) {
        const found = findCategoryInTree(parent.children, targetId);
        if (found) return found;
      }
    }

    return null;
  };

  const addSubTree = async(target: Category, source: Category) => {
    if ( source.contentCategory && source.contents) {
    let newContents: Content[] = [];
      for (const content of source.contents ) {
         const response = await api.post('/content', {
             parentId: target.id,
             text: content.text,
             subcase: content.subcase,
             id: ""
           });
         const newContent = response.data;
         newContents.push(newContent);
      };

      target.contents = newContents;
    }
    else if ( source.children ){
      for (const cat of source.children ) {
        const response = await api.post('/categories', {
          parentId: target.id,
          name: cat.name,
          description: cat.description
        });
        if (!response.data) throw new Error('Failed to create category');
        const newCategory = response.data;

        target.children = [...(target.children || []), newCategory];
        target.contentCategory = false;

        await addSubTree(newCategory, cat);
       }
    }
  };

  const handleUndo = async () => {
    const operation = historyService.undo();
    if (!operation) return;
    setLoading(true);
    try {
      switch (operation.type) {
        case 'splitCategory': {
            const { firstCategory, secondCategory, parentId, tempCategory, movedContentsFirst, movedContentsSecond} = operation.data;
            await deleteCategory(firstCategory.id);
            await deleteCategory(secondCategory.id);

            if ( tempCategory.id !== firstCategory.id ) {
                await deleteCategory(tempCategory.id);
            }

             let newContents: {content: Content, index: number}[] = [];
             for (const {content, oldContentId, index} of movedContentsFirst ) {
                const response = await api.post('/content', {
                    parentId: parentId,
                    text: content.text,
                    subcase: content.subcase,
                  id: oldContentId
                  });

                const newContent = response.data;
                newContents.push({content: newContent, index: index});
            };

            for (const {content, oldContentId, index} of movedContentsSecond) {
                const response = await api.post('/content', {
                    parentId: parentId,
                    text: content.text,
                    subcase: content.subcase,
                 id: oldContentId
                  });
                const newContent = response.data;
                newContents.push({content: newContent, index: index});
            };

            for (const {content, index} of newContents) {
             await api.put(`/content/moveto`,
                 {
                     contentId: content.id,
                     moveTo: index
                 },
                 {
                   headers: {
                     'Content-Type': 'application/x-www-form-urlencoded'
                   }
                 });
             };

            newContents.sort((a, b) => a.index - b.index);
            setCategories(prev => {
                const newCategories = [...prev];
                const parent = findParentCategoryInTree(newCategories, firstCategory.id);
                if (parent) {
                  parent.contents = newContents.map(c => c.content);
                  parent.children = [];
                  parent.contentCategory = true;

                  // Select the new category and expand the parent
                  setTimeout(() => {
                    setSelectedCategory(parent);
                    setSelectedCategories([]);
                    setExpandedNodes(prev => [...prev, parent.id]);
                  }, 0);
                }

                return newCategories;
            });
            break;
        }
        case 'deleteCategory': {
          const { category, parentId, index } = operation.data;
           const response = await api.post('/categories', {
              parentId,
              name: category.name,
              description: category.description
            });
            if (!response.data) throw new Error('Failed to create category');
            const newCategory = response.data;
            await api.put('/categories/moveto',
                {
                    categoryId: newCategory.id,
                    moveTo: index
                },
                {
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                  }
                }
             );
          if (parentId) {
            const parent = findCategoryInTree(categories, parentId);
            if (parent) {
              if (index >= 0) {
                if (parent.children) {
                  parent.children.splice(index, 0, newCategory);
                }
              } else {
                if (parent.children) {
                  parent.children.push(newCategory);
                }
              }
              setCategories([...categories]);
            }
          }

         if (category.children && category.children.length > 0 || category.contents && category.contents.length > 0) {
            await addSubTree(newCategory, category);
         }

          break;
        }
        case 'addCategory': {
          const { category } = operation.data;
          await deleteCategory(category.id);
          setCategories(prev => {
            const newCategories = [...prev];
            const parent = findParentCategoryInTree(newCategories, category.id);
            if (parent) {
              parent.children = parent.children?.filter(child => child.id !== category.id);
            }
            return newCategories;
          });
          break;
        }
        case 'renameCategory': {
            const { category, oldName, oldDescription } = operation.data;
            const response = await api.put(`/categories`, {
                    parentId: category.id,
                    name: oldName,
                    description: oldDescription
                }
              );
            const renamedCategory = response.data;
            const updatedCategory = await fetchCategoryWithChildren(renamedCategory.parentId);

           setCategories(prevCategories => {
             const updatedCategories = [...prevCategories];
                const newCategories = updateCategoryInTree(updatedCategories, updatedCategory.id, () => updatedCategory);

                // Find and update the selected category within the same state update
                if (selectedCategory) {
                    const cat = findCategoryInTree(newCategories, selectedCategory.id);
           if (cat) {
                        // Use setTimeout to ensure this happens after the state update
               setTimeout(() => {
                    setSelectedCategory(cat);
                  }, 0);
            }
                }

                return newCategories;
            });
            break;
        }
        case 'pasteCategory':
        case 'dropCategory': {
          const { category, oldCategoryId, oldParentId, newParentId, oldIndex, newIndex } = operation.data;
          await deleteCategory(category.id);

          setCategories(prev => {
            const newCategories = [...prev];
            const parent = findCategoryInTree(newCategories, newParentId);
            if (parent) {
              parent.children = parent.children?.filter(child => child.id !== category.id);
            }
            return newCategories;
          });

          if ( oldIndex >= 0 ) {
            const response = await api.post('/categories', {
              parentId: oldParentId,
              name: category.name,
              description: category.description
            });

            if (!response.data) throw new Error('Failed to create category');
            const newCategory = response.data;

            await api.put(`/categories/moveto`,
                {
                    categoryId: newCategory.id,
                    moveTo: oldIndex
                },
                {
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                  }
                }
             );

               await addSubTree(newCategory, category);


              setCategories(prevCategories => {
                const updatedCategories = [...prevCategories];
                const parent = findCategoryInTree(updatedCategories, oldParentId);
                if (parent) {
                    // Remove any existing reference to the dropped category
                    if (parent.children) {
                        parent.children = parent.children.filter(child => child.id !== category.id && child.id !== newCategory.id);
                    }
                    // Now insert the new category at the correct index
                    if (parent.children) {
                        parent.children.splice(oldIndex, 0, newCategory);
                    }
                }
                return updatedCategories;
              });

           }
          break;
        }
        case 'groupCategories': {
          const { originalParentId, newParent, groupedCategories } = operation.data
          const movedCategories: { category: Category; index: number }[] = [];
          for ( const {name, index} of groupedCategories ) {
            const response = await api.put(`/categories/move`, {
                categoryId: newParent.id + ":" +name,
                newParentId: originalParentId
              },
              {
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded'
              }
             });
             const movedCategory = response.data;
             const movedCategoryWithChildren = await fetchCategoryWithChildren(movedCategory.id);
               if (!movedCategoryWithChildren) {
                 throw new Error('Could not fetch updated category tree');
               }
             movedCategories.push({category: movedCategoryWithChildren, index: index});
          }

          for ( const {category, index} of movedCategories ) {
            await api.put(`/categories/moveto`,
                {
                    categoryId: category.id,
                    moveTo: index
                },
                {
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                  }
                }
             );
              setCategories(prev => {
                 const newCategories = [...prev];
                 const parent = findCategoryInTree(newCategories, originalParentId);
                 if (parent) {
                     if (parent.children) {
                       parent.children.splice(index, 0, category);
                     }
                 }
                 return newCategories;
             });
            }

            await deleteCategory(newParent.id);

            setCategories(prev => {
                const newCategories = [...prev];
                const parent = findParentCategoryInTree(newCategories, newParent.id);
                if (parent) {
                  parent.children = parent.children?.filter(child => child.id !== newParent.id);
                }
                return newCategories;
              });
          setSelectedCategory(selectedCategory);
          setSelectedCategories([]);
          break;
        }
        case 'flattenCategory': {
            const { parentId, category, index, flattenCategories, originalCategories } = operation.data;
             const response = await api.post('/categories', {
                  parentId,
                  name: category.name,
                  description: category.description
                });

             const createdCategory = response.data;

             await api.put(`/categories/moveto`,
                 {
                     categoryId: createdCategory.id,
                     moveTo: index
                 },
                 {
                     headers: {
                         'Content-Type': 'application/x-www-form-urlencoded'
                     }
                 }
             );

            // After moving categories, wait a bit and fetch again
            // Move categories sequentially instead of in parallel
            for (const cat of flattenCategories) {
                try {
                    const response = await api.put(`/categories/move`, {
                        categoryId: cat.id,
                        newParentId: createdCategory.id
                    },
                    {
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded'
                        }
                    });


                } catch (error) {
                    console.error(`Failed to move category ${cat.id}:`, error);
                    throw error;
                }
            }

            // Wait a bit more before fetching the updated tree
            await new Promise(resolve => setTimeout(resolve, 200));

            const updatedTree = await fetchCategoryWithChildren(createdCategory.id);
                 if (!updatedTree) {
                   throw new Error('Could not fetch updated category tree');
                 }

            setCategories(prev => {
                const parent = findCategoryInTree(prev, parentId);
                if (!parent) return prev;

                // Get all categories that should remain at the parent level
                const remainingChildren = parent.children?.filter(child =>
                    !flattenCategories.some((flattenCat: Category) =>
                        child.id.includes(flattenCat.name) // Simple name matching
                    )
                ) || [];

                // Create the new structure
                const newParent = {
                    ...parent,
                    children: [
                        ...remainingChildren
                    ]
                };

                // Insert at specific index
                newParent.children.splice(index, 0, updatedTree);

                // Replace the parent in the tree
                const updateTree = (categories: Category[]): Category[] => {
                    return categories.map(category => {
                        if (category.id === parentId) {
                            return newParent;
                        } else {
                            return {
                                ...category,
                                children: category.children ? updateTree(category.children) : []
                            };
                        }
                    });
                };

                const result = updateTree(prev);

                return result;
            });
            break;
        }
        case 'mergeCategories': {
            const { parentId, newCategory, mergedCategories } = operation.data;
            await deleteCategory(newCategory.id);
            setCategories(prev => {
                const newCategories = [...prev];
                const parent = findParentCategoryInTree(newCategories, newCategory.id);
                if (parent) {
                    parent.children = parent.children?.filter(child => child.id !== newCategory.id);
                }
                return newCategories;
            });

            // Create a map to store created categories with their indices
            const createdCategories = new Map<number, Category>();

            for (const {category, index} of mergedCategories) {
                const response = await api.post('/categories', {
                  parentId,
                  name: category.name,
                  description: category.description
                });

                const createdCategory = response.data;
                createdCategories.set(index, createdCategory);

                if ( category.contents.length > 0 ) {
                let newContents: Content[] = [];
                    for (const content of category.contents) {
                    const response = await api.post('/content', {
                        parentId: createdCategory.id,
                        text: content.text,
                        subcase: content.subcase,
                        id: ""
                      });
                    const newContent = response.data;
                    newContents.push(newContent);
                    }

                    // Update the created category with its contents
                    createdCategory.contents = newContents;
                }
            }

            // Add all categories at once in the correct order
            setCategories(prev => {
                const newCategories = [...prev];
                const parent = findCategoryInTree(newCategories, parentId);
                if (parent) {
                    // Sort the indices to maintain order
                    const sortedIndices = Array.from(createdCategories.keys()).sort((a, b) => a - b);
                    for (const index of sortedIndices) {
                        const category = createdCategories.get(index);
                        if (category) {
                            if (parent.children) {
                                parent.children.splice(index, 0, category);
                            }
                        }
                    }
                }
                return newCategories;
              });

            // Update backend order
            for (const {category, index} of mergedCategories) {
                await api.put(`/categories/moveto`,
                    {
                        categoryId: category.id,
                        moveTo: index
                    },
                    {
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded'
                        }
                    }
                );
            }

            const parentCategory = findCategoryInTree(categories, parentId);
            if (parentCategory) {
                setSelectedCategory(parentCategory);
            }
            setSelectedCategories([]);
            break;
        }
        case 'generateSubtree': {
            const { originalCategory, originalContents, tempCategory, generatedTree, renamedCategoryId, originalCategoryName } = operation.data;

            // Find the current category (either renamed or original)
            const currentCategoryId = renamedCategoryId || originalCategory.id;
            const currentCategory = findCategoryInTree(categories, currentCategoryId);

            if (!currentCategory) {
                throw new Error('Could not find current category for undo');
            }

            // Step 1: Delete all categories under the main category
            if (currentCategory.children && currentCategory.children.length > 0) {
                for (const child of currentCategory.children) {
                    try {
                        await deleteCategory(child.id);
                    } catch (err) {
                        console.error('Error deleting generated category:', err);
                    }
                }
            }

            // Step 2: Rename it back if it was renamed
            if (renamedCategoryId && originalCategoryName) {
                try {
                    // parentId is actually the category ID being renamed
                    await api.put('/categories', {
                        parentId: renamedCategoryId, // This is the category ID, not the parent ID
                        name: originalCategoryName,
                        description: originalCategory.description || ''
                    });
                } catch (err) {
                    console.error('Error renaming category back:', err);
                }
            }

            // Step 3: Create all the content in the renamed category
            for (const content of originalContents) {
                try {
                    await api.post('/content', {
                        parentId: originalCategory.id,
                        text: content.text,
                        subcase: content.subcase,
                        id: ''
                    });
                } catch (err) {
                    console.error('Error restoring original content:', err);
                }
            }

            // Step 4: Refresh the categories from the backend to sync the frontend state
            try {
                    const updatedCategory = await fetchCategoryWithChildren(originalCategory.id);

                    setCategories(prevCategories => {
                        const updatedCategories = [...prevCategories];

                        if ( !originalCategory.parentId ) return [updatedCategory];

                        // Category has a parent - find and update the parent
                        const parentCategory = findCategoryInTree(updatedCategories, originalCategory.parentId);
                        if (parentCategory && parentCategory.children) {
                            // Find and replace the old category with the updated one
                            const childIndex = parentCategory.children.findIndex(child => child.id === currentCategoryId);
                            if (childIndex !== -1) {
                                parentCategory.children[childIndex] = updatedCategory;
                            }
                        }


                        return updatedCategories;
                    });

                    // Select the restored category
                    setSelectedCategory(updatedCategory);
                    setSelectedCategories([]);
                    setExpandedNodes(prev => [...prev, originalCategory.id]);

                } catch (err) {
                    console.error('Error refreshing category:', err);
                }

            break;
        }
        case 'moveCategoryUp': {
          const { parentId, firstIndex, lastIndex, direction } = operation.data
          await api.put('/categories/movechild', {
              categoryId: parentId,
              firstIndex: firstIndex -1,
              lastIndex: lastIndex -1,
              direction: "down"
            },
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            });


             setCategories(prev => {
                const newCategories = [...prev];
                const parent = findCategoryInTree(newCategories, parentId);
                if (parent && typeof parent.children !== 'undefined') {
                   for (let i = lastIndex; i >= firstIndex; i--) {
                       [parent.children[i - 1], parent.children[i]] = [parent.children[i], parent.children[i - 1]];
                   }
                }
                return newCategories;
              });
            break;
        }
         case 'moveCategoryDown': {
              const { parentId, firstIndex, lastIndex, direction } = operation.data

              await api.put('/categories/movechild', {
                  categoryId: parentId,
                  firstIndex: firstIndex + 1,
                  lastIndex: lastIndex + 1,
                  direction: "up"
                },
                {
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                  }
                });

                setCategories(prev => {
                   const newCategories = [...prev];
                   const parent = findCategoryInTree(newCategories, parentId);
                   if (parent && typeof parent.children !== 'undefined') {
                     for (let i = firstIndex; i <= lastIndex; i++) {
                        [parent.children[i], parent.children[i + 1]] = [parent.children[i + 1], parent.children[i]];
                    }
                   }
                   return newCategories;
                 });
                break;
            }
        case 'moveCategoryTo' : {
            const { categoryId, fromIndex, toIndex } = operation.data
            await api.put('/categories/moveto',
                {
                    categoryId: categoryId,
                    moveTo: fromIndex
                },
                {
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                  }
                }
            );

              setCategories(prev => {
                 const newCategories = [...prev];
                 const parent = findParentCategoryInTree(newCategories, categoryId);
                 if (parent && parent.children) {
                   const child = parent.children[toIndex];
                   parent.children?.splice(toIndex, 1);
                   parent.children?.splice(fromIndex, 0, child);
                 }
                 return newCategories;
               });
               break;
        }
        case 'deleteContent': {
          const contents = Array.isArray(operation.data) ? operation.data : [operation.data];
          const undoContents: { content: Content; index: number }[] = [];
          for (const { content, categoryId, index } of contents) {
            const response = await api.post('/content', {
              parentId: categoryId,
              text: content.text,
              subcase: content.subcase,
              id: content.id
            });
            if (!response.data) throw new Error('Failed to create content');
            const newContent = response.data;
            undoContents.push({content: newContent, index: index});
          }

          for (const { content, index } of undoContents) {
            await api.put(`/content/moveto`,
            {
                contentId: content.id,
                moveTo: index
            },
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            });

            setCategories(prev => {
                const newCategories = [...prev];
                const parent = findCategoryInTree(newCategories, contents[0].categoryId);
                if (parent) {
                  if (index >= 0) {
                    if (parent.contents) {
                      parent.contents.splice(index, 0, content);
                    }
                  } else {
                    if (parent.contents) {
                      parent.contents.push(content);
                    }
                  }
                }
                return newCategories;
            });
          }
          break;
        }
        case 'addContent': {
          const { content, categoryId } = operation.data;
          await api.delete('/content', { params: { contentId: content.id } });

          setCategories(prev => {
            const newCategories = [...prev];
            const parent = findCategoryInTree(newCategories, categoryId);
            if (parent) {
              parent.contents = parent.contents?.filter(child => child.id !== content.id);
            }
            return newCategories;
          });
          setSelectedCategory(selectedCategory);
          break;
        }
        case 'pasteContent':
        case 'dropContent': {
          const contents = Array.isArray(operation.data) ? operation.data : [operation.data];
          for (const { content, oldContentId, oldParentId, oldIndex } of contents) {
            await api.delete('/content', { params: { contentId: content.id } });
            setCategories(prev => {
              const newCategories = [...prev];
              const parent = findCategoryInTree(newCategories, content.parentId);
              if (parent) {
                parent.contents = parent.contents?.filter(child => child.id !== content.id);
              }
              return newCategories;
            });
          }

          if ( operation.isBatch ) {
             const undoContents: { content: Content; index: number }[] = [];
             for (const { content, oldContentId, oldParentId, oldIndex } of contents) {
                const response = await api.post('/content', {
                   parentId: oldParentId,
                   text: content.text,
                   subcase: content.subcase,
                   id: oldContentId
                 });
                 if (!response.data) throw new Error('Failed to create content');
                 const newContent = response.data;
                 undoContents.push({content: newContent, index: oldIndex});
             }

             for (const { content, index } of undoContents) {
                await api.put(`/content/moveto`,
                {
                    contentId: content.id,
                    moveTo: index
                },
                {
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                  }
                });

                setCategories(prev => {
                    const newCategories = [...prev];
                    const parent = findCategoryInTree(newCategories, content.parentId);

                    if (parent) {
                      if (index >= 0) {
                        if (parent.contents) {
                          parent.contents.splice(index, 0, content);
                        }
                      } else {
                        if (parent.contents) {
                          parent.contents.push(content);
                        }
                      }
                    }

                    return newCategories;
                });

                const newParent = findCategoryInTree(categories, contents[0].oldParentId);
                setSelectedCategory(newParent);
             }
          }

          break;
        }
        case 'updateContent': {
          const { content, prevContent, categoryId } = operation.data;
          const response = await api.post('/content', {
            parentId: categoryId,
            text: prevContent.text,
            subcase: prevContent.subcase,
            id: prevContent.id
          });

           setCategories(prev => {
              const newCategories = [...prev];
              const parent = findCategoryInTree(newCategories, categoryId);
              if (parent && parent.contents) {
                const index = parent.contents.findIndex(child => child.id === content.id);
                parent.contents[index] = prevContent;
              }
              return newCategories;
            });
          break;
        }
        case 'moveContentUp': {
          const { parentId, firstIndex, lastIndex, direction } = operation.data
          await api.put('/categories/movechild', {
              categoryId: parentId,
              firstIndex: firstIndex - 1,
              lastIndex: lastIndex - 1,
              direction: "down"
            },
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            });

            setCategories(prev => {
                const newCategories = [...prev];
                const parent = findCategoryInTree(newCategories, parentId);
                if (parent && typeof parent.contents !== 'undefined') {
                   for (let i = lastIndex; i >= firstIndex; i--) {
                       [parent.contents[i - 1], parent.contents[i]] = [parent.contents[i], parent.contents[i - 1]];
                   }
                }
                return newCategories;
              });

            break;
        }
         case 'moveContentDown': {
          const { parentId, firstIndex, lastIndex, direction } = operation.data
          await api.put('/categories/movechild', {
              categoryId: parentId,
              firstIndex: firstIndex + 1,
              lastIndex: lastIndex + 1,
              direction: "up"
            },
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            });

            setCategories(prev => {
                const newCategories = [...prev];
                const parent = findCategoryInTree(newCategories, parentId);
                if (parent && typeof parent.contents !== 'undefined') {
                  for (let i = firstIndex; i <= lastIndex; i++) {
                     [parent.contents[i], parent.contents[i + 1]] = [parent.contents[i + 1], parent.contents[i]];
                 }
                }
                return newCategories;
              });
            break;
        }
        case 'dragCategoryTo':
        case 'dragContentTo': {
              const { parentId, firstIndex, lastIndex, toIndex } = operation.data

              await api.put('/categories/movechildto', {
                  categoryId: parentId,
                  firstIndex: toIndex > lastIndex ? toIndex - (lastIndex - firstIndex) : toIndex,
                  lastIndex: toIndex > lastIndex ? toIndex : toIndex + (lastIndex - firstIndex),
                  moveToIndex: toIndex > lastIndex ? firstIndex : lastIndex
                },
                {
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                  }
                });

                const updatedCategory = await fetchCategoryWithChildren(parentId);

                setCategories(prevCategories => {
                   const updatedCategories = [...prevCategories];
                   return updateCategoryInTree(updatedCategories, parentId, () => updatedCategory);
                 });

                 setTimeout(() => {
                      setSelectedCategory(updatedCategory);
                      //setSelectedCategories([]);
                      setExpandedNodes(prev => [...prev, updatedCategory.id]);
                    }, 0);
                break;
            }
            case 'renameSubcase': {
                 const { oldName, newName } = operation.data;
                 setLoading(true);
                  const response = await api.put('/documents/subcases/rename', {
                    oldSubcase: newName,
                    newSubcase: oldName
                  },{ params: { documentId: activeDocument?.id} });

                 setLoading(false);
                 if (response.status === 200) {
                     // Update the document's subcases list

                    if (activeDocument) {
                           await handleOpenDocument(activeDocument);
                           activeDocument.subcases.forEach((item, i) => { if (item == newName) activeDocument.subcases[i] = oldName; });
                           Array.from(new Set(activeDocument.subcases));
                    }
                 }
                break;
            }
      }
    } catch (error) {
      console.error('Error undoing operation:', error);
      alert('Failed to undo operation');
    }
    setLoading(false);
  };

  const handleRedo = async () => {
    const operation = historyService.redo();
    if (!operation) return;

    setLoading(true);
    try {
      switch (operation.type) {
        case 'splitCategory': {
            const { firstCategory, secondCategory, parentId, tempCategory, movedContentsFirst, movedContentsSecond} = operation.data;
             var response = await api.post('/categories', {
                parentId,
                name: firstCategory.name,
                description: firstCategory.description
              });
              if (!response.data) throw new Error('Failed to create category');
              const newFirstCategory = response.data;
              response = await api.post('/categories', {
                  parentId,
                  name: secondCategory.name,
                  description: secondCategory.description
              });
              if (!response.data) throw new Error('Failed to create category');
              const newSecondCategory = response.data;

             let newChildren: Category[] = [];
             let newChildrenIds: string[] = [];
             let newMovedContentsToFirst: Content[] = [];
             let newMovedChildrenIdsToFirst: string[] = [];
             let newMovedContentsToSecond: Content[] = [];
             let newMovedChildrenIdsToSecond: string[] = [];

             newChildren.push(newFirstCategory);
             newChildrenIds.push(newFirstCategory.id);
             newChildren.push(newSecondCategory);
             newChildrenIds.push(newSecondCategory.id);

             let newTempCategory: Category = newFirstCategory;
             if ( tempCategory.id !== firstCategory.id ) {
                 response = await api.post('/categories', {
                   parentId,
                   name: tempCategory.name,
                   description: tempCategory.description
                 });
                 if (!response.data) throw new Error('Failed to create category');
                 newTempCategory = response.data;
                 newChildren.push(tempCategory);
                 newChildrenIds.push(tempCategory.id);
             }


             for (const {content, oldContentId, index} of movedContentsFirst) {
                 const response = await api.put('/content/move', {
                    contentId: oldContentId,
                    newParentId: newTempCategory?.id,
                    newContentId: content.id
                  },
                  {
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded'
                  }
                 });
                 const movedContent = response.data;
                 newMovedContentsToFirst.push(movedContent);
                 newMovedChildrenIdsToFirst.push(movedContent.id);
             };

             for (const {content, oldContentId, index} of movedContentsSecond) {
                  const response = await api.put(`/content/move`, {
                     contentId: oldContentId,
                     newParentId: newSecondCategory?.id,
                     newContentId: content.id
                   },
                   {
                     headers: {
                       'Content-Type': 'application/x-www-form-urlencoded'
                   }
                  });
                  const movedContent = response.data;
                  newMovedContentsToSecond.push(movedContent);
                  newMovedChildrenIdsToSecond.push(movedContent.id);
              };



            const parent = findCategoryInTree(categories, parentId);
            if ( !parent )
                throw new Error('Failed to find category');

            const updatedParent = {
               ...parent,
               children: newChildren,
               contentCategory: false,
               contents: []
            };

             // Update the categories state with the new tree
             setCategories(prevCategories => {
               const updatedCategories = [...prevCategories];

               // Update the tree with the modified parent
               return updateCategoryInTree(updatedCategories, parentId, () => updatedParent);
             });

             const updateTemp = {
               ...newTempCategory,
               contents: newMovedContentsToFirst
             };

             const tempId = newTempCategory?.id;
             // Update the categories state with the new tree
             setCategories(prevCategories => {
               const updatedCategories = [...prevCategories];

               // Update the tree with the modified parent
               return updateCategoryInTree(updatedCategories, tempId, () => updateTemp);
             });

             const updateSecond = {
                ...newSecondCategory,
                contents: newMovedContentsToSecond
              };

              // Update the categories state with the new tree
              setCategories(prevCategories => {
                const updatedCategories = [...prevCategories];

                // Update the tree with the modified parent
                return updateCategoryInTree(updatedCategories, newSecondCategory?.id, () => updateSecond);
              });

             // Select the parent category and expand it
             setSelectedCategory(updatedParent);
             setSelectedCategories([]);
             setExpandedNodes(prev => [...prev, parentId]);
             break;
        }
        case 'deleteCategory': {
          const { category, parentId } = operation.data;
          await deleteCategory(category.id);
          setCategories(prev => {
            const newCategories = [...prev];
            const parent = findParentCategoryInTree(newCategories, category.id);
            if (parent) {
              parent.children = parent.children?.filter(child => child.id !== category.id);
            }
            return newCategories;
          });

           const newParent = findCategoryInTree(categories, parentId);
           setSelectedCategory(newParent);
          break;
        }
        case 'addCategory': {
          const { category, parentId } = operation.data;
          const response = await api.post('/categories', {
            parentId,
            name: category.name,
            description: category.description
          });
          if (!response.data) throw new Error('Failed to create category');
          const newCategory = response.data;
          setCategories(prev => {
            const newCategories = [...prev];
            const parent = findCategoryInTree(newCategories, parentId);
            if (parent) {
              parent.children = [...(parent.children || []), newCategory];
            }
            return newCategories;
          });
          break;
        }
        case 'renameCategory': {
            const { category, oldId } = operation.data;
            const response = await api.put('/categories', {
                    parentId: oldId,
                    name: category.name,
                    description: category.description
                }
              );

            const renamedCategory = response.data;
            const updatedCategory = await fetchCategoryWithChildren(renamedCategory.parentId);

           setCategories(prevCategories => {
             const updatedCategories = [...prevCategories];
               const newCategories = updateCategoryInTree(updatedCategories, updatedCategory.id, () => updatedCategory);

               // Find and update the selected category within the same state update
               if (selectedCategory) {
                   const cat = findCategoryInTree(newCategories, selectedCategory.id);
                   if (cat) {
                       // Use setTimeout to ensure this happens after the state update
            setTimeout(() => {
                 setSelectedCategory(cat);
               }, 0);
            }
               }

               return newCategories;
           });
            break;
        }
        case 'pasteCategory':
        case 'dropCategory':  {
          const { category, oldCategoryId, oldParentId, newParentId, oldIndex, newIndex } = operation.data;
          var newCategory:Category = category;
          if ( oldIndex >= 0) {
           const response = await api.put('/categories/move', {
             categoryId: oldCategoryId,
             newParentId: newParentId
           },
            {
             headers: {
               'Content-Type': 'application/x-www-form-urlencoded'
             }
           });
           newCategory = response.data;
           setCategories(prev => {
               const newCategories = [...prev];
               const parent = findCategoryInTree(newCategories, oldParentId);
               if (parent) {
                 parent.children = parent.children?.filter(child => child.id !== oldCategoryId);
               }
               return newCategories;
           });
          }
          else {
              const response = await api.post('/categories', {
                parentId: newParentId,
                name: category.name,
                description: category.description
              });
              if (!response.data) throw new Error('Failed to create category');
              newCategory = response.data;
          }

          await addSubTree(newCategory, category);

          setCategories(prev => {
            const updatedCategories = [...prev];
           const targetCategory = findCategoryInTree(updatedCategories, newParentId);
           if ( targetCategory) {
              // Update the target parent with the new category
             const updatedTargetParent = {
               ...targetCategory,
               children: [...(targetCategory.children || []), newCategory]
             };
             const newCategories = updateCategoryInTree(updatedCategories, targetCategory.id, () => updatedTargetParent);
             return newCategories;
            }
             return [];
          });

          setSelectedCategory(selectedCategory);
          break;
        }
        case 'groupCategories': {
          const { originalParentId, newParent, groupedCategories } = operation.data
          const response = await api.post('/categories', {
              parentId: originalParentId,
              name: newParent.name,
              description: newParent.description
            });
          if (!response.data) throw new Error('Failed to create category');
          const newCategory = response.data;

          const movedCategories: Category[] = [];
          for ( const {name, index} of groupedCategories ) {
            const response = await api.put('/categories/move', {
                categoryId: originalParentId + ":" + name,
                newParentId: newCategory?.id
              },
              {
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded'
              }
             });
             const movedCategory = response.data;
             movedCategories.push(movedCategory);
          }

          const updatedTree = await fetchCategoryWithChildren(originalParentId);
            if (!updatedTree) {
              throw new Error('Could not fetch updated category tree');
            }

            // Update the state with the complete tree
            setCategories(prev => {
              const newCategories = [...prev];
              const parent = findParentCategoryInTree(newCategories, originalParentId);
              if ( !parent ) return [updatedTree];
              if (parent && parent.children) {
                const index = parent.children.findIndex(cat => cat.id === updatedTree.id);
                if (index !== -1) {
                  parent.children[index] = updatedTree;
                } else {
                  parent.children.push(updatedTree);
                }
              }
              return newCategories;
            });
          setSelectedCategory(newCategory);
          setSelectedCategories([]);
          setExpandedNodes(prev => [...prev, newCategory]);
          break;
        }
        case 'flattenCategory': {
            const { parentId, category, index, flattenCategories, originalCategories } = operation.data;
             var movedCategories: Category[] = [];
             for (const cat of originalCategories) {
                 try {
                     const response = await api.put(`/categories/move`, {
                         categoryId: cat.id,
                         newParentId: parentId
                     },
                     {
                         headers: {
                             'Content-Type': 'application/x-www-form-urlencoded'
                         }
                     });

                  const movedCat = response.data;
                  const updatedCat = await fetchCategoryWithChildren(movedCat.id);
                  movedCategories.push(updatedCat)

                 } catch (error) {
                     console.error(`Failed to move category ${cat.id}:`, error);
                     throw error;
                 }
             }

           await deleteCategory(category.id);

           const parent = findCategoryInTree(categories, parentId);
           if (!parent) return;
          const firstIndex = parent.children!.length - 1;
          const lastIndex = firstIndex + flattenCategories.length - 1;
          await api.put(`/categories/movechildto`, {
                   categoryId: parentId,
                   firstIndex,
                   lastIndex,
                   moveToIndex: index
                 },
                 {
                   headers: {
                     'Content-Type': 'application/x-www-form-urlencoded'
                   }
                 });

             setCategories(prev => {
                 const newCategories = [...prev];
                 const parent = findParentCategoryInTree(newCategories, category.id);
                 if (parent && parent.children) {

                     // Remove the selected category from its current position
                     const filteredChildren = parent.children.filter(child => child.id !== category.id);

                     // Insert the children at the specific index
                     filteredChildren.splice(index, 0, ...(movedCategories || []));
                     parent.children = filteredChildren;
                 }

                 setSelectedCategory(parent);
                 return newCategories;
             });
            break;
        }
        case 'mergeCategories': {
            const { parentId, newCategory, mergedCategories } = operation.data;
            const categories = mergedCategories.map((cat: {category:Category, index:number}) => cat.category);
            for (const category of categories) {
              await deleteCategory(category.id);  // ← Sequential deletion
            }

             const response = await api.post('/categories', {
                parentId: parentId,
                name: newCategory.name,
                description: newCategory.description
              });

              const addedCategory = response.data;

            setCategories(prev => {
                 const newCategories = [...prev];
                 const parent = findCategoryInTree(newCategories, parentId);
                 if (parent && parent.children) {
                   const removeIds = categories.map((obj: Category) => obj.id);
                   parent.children = [
                     ...(parent.children || []).filter((child: Category) => !removeIds.includes(child.id)),
                     addedCategory
                   ];
                 }
                 return newCategories;
               });

               let aggregatedContent: Content[] = []
              for ( const category of categories) {
                   if ( !category.contents ) continue;
                   for (const content of category.contents) {
                       if ( !aggregatedContent.some(c => c.subcase === content.subcase && c.text === content.text) )
                           aggregatedContent.push(content);
                   }
               }

               let newContents: Content[] = [];
               for (const content of aggregatedContent) {
                  const response = await api.post(`/content`, {
                       parentId: addedCategory.id,
                       text: content.text,
                       subcase: content.subcase
                     });
                  newContents.push(response.data);
               }

                setCategories(prev => {
                  const newCategories = [...prev];
                  const parent = findCategoryInTree(newCategories, addedCategory.id);
                  if (parent) {
                    parent.contents = newContents;
                  }
                  return newCategories;
                });
                setSelectedCategory(addedCategory);
                setSelectedCategories([]);
            break;
        }
        case 'generateSubtree': {
            const { originalCategory, originalContents, tempCategory, generatedTree, renamedCategoryId, originalCategoryName } = operation.data;

            const contentsToDelete = originalContents || [];
            if (contentsToDelete.length > 0) {
                for (const content of contentsToDelete) {
                    try {
                        await api.delete('/content', {
                            params: { contentId: content.id }
                        });
                    } catch (err) {
                        console.error('Failed to delete content:', content.id, err);
                    }
                }
            }

            let targetCategory = originalCategory
            // Step 1: Rename category to the generated name if it was renamed in the original operation
            if (renamedCategoryId && originalCategoryName) {
                try {
                    const response = await api.put('/categories', {
                        parentId: originalCategory.id, // This is actually the category ID being renamed
                        name: generatedTree.name, // Use the generated name from the tree
                        description: generatedTree.description || ''
                    });

                    targetCategory = response.data;
                } catch (err) {
                    console.error('✗ Failed to rename category to generated name:', err);

                }
            }

            // Step 2: Create temp category and move original content if it was created in the original operation
            if (tempCategory) {
                try {

                    // Create temp category
                    const tempCategoryRes = await api.post('/categories', {
                        parentId: targetCategory.id,
                        name: `Original Content - ${originalCategory.name}`,
                        description: 'Temporary category containing original content'
                    });
                    const newTempCategory = tempCategoryRes.data;

                    // Move all original content to temp category
                    const contentsToMove = originalContents || [];
                    for (const content of contentsToMove) {
                        try {
                            await api.put('/content/move', { params: { contentId: content.id,  newParentId: newTempCategory.id} });
                        } catch (err) {
                            console.error('✗ Failed to move content:', content.id, err);
                            // Continue with other content even if one fails
                        }
                    }
                } catch (err) {
                    console.error('✗ Failed to create temp category or move content:', err);
                }
            }

            // Use addSubTree to build the tree
            await addSubTree(targetCategory, generatedTree);

            // Step 4: Update frontend state
            try {
                const updatedCategory = await fetchCategoryWithChildren(targetCategory.id);

                setCategories(prevCategories => {
                    const updatedCategories = [...prevCategories];

                    if ( !updatedCategory.parentId ) return [updatedCategory];

                    // Category has a parent - find and update the parent
                    const parentCategory = findCategoryInTree(updatedCategories, targetCategory.parentId);
                    if (parentCategory && parentCategory.children) {
                        // Find and replace the old category with the updated one
                        const childIndex = parentCategory.children.findIndex(child => child.id === originalCategory.id);
                        if (childIndex !== -1) {
                            parentCategory.children[childIndex] = updatedCategory;
                        }
                    }


                    return updatedCategories;
                });

                // Select the restored category
                setSelectedCategory(updatedCategory);
                setSelectedCategories([]);
                setExpandedNodes(prev => [...prev, originalCategory.id]);

            } catch (err) {
                console.error('Error refreshing category:', err);
            }

            break;
        }
        case 'moveCategoryUp': {
          const { parentId, firstIndex, lastIndex, direction } = operation.data
          await api.put('/categories/movechild', {
              categoryId: parentId,
              firstIndex: firstIndex,
              lastIndex: lastIndex,
              direction: "up"
            },
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            });


             setCategories(prev => {
                const newCategories = [...prev];
                const parent = findCategoryInTree(newCategories, parentId);
                if (parent && typeof parent.children !== 'undefined') {
                  for (let i = firstIndex; i <= lastIndex; i++) {
                     [parent.children[i - 1], parent.children[i]] = [parent.children[i], parent.children[i - 1]];
                 }
                }
                return newCategories;
              });
            break;
        }
        case 'moveCategoryDown': {
          const { parentId, firstIndex, lastIndex, direction } = operation.data
          await api.put('/categories/movechild', {
              categoryId: parentId,
              firstIndex: firstIndex,
              lastIndex: lastIndex,
              direction: "down"
            },
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            });

            setCategories(prev => {
                const newCategories = [...prev];
                const parent = findCategoryInTree(newCategories, parentId);
                if (parent && typeof parent.children !== 'undefined') {
                  for (let i = lastIndex; i >= firstIndex; i--) {
                     [parent.children[i], parent.children[i + 1]] = [parent.children[i + 1], parent.children[i]];
                 }
                }
                return newCategories;
              });
            break;
        }
        case 'moveCategoryTo' : {
            const { categoryId, fromIndex, toIndex } = operation.data
            await api.put('/categories/moveto',
                {
                    categoryId: categoryId,
                    moveTo: toIndex
                },
                {
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                  }
                }
            );

              setCategories(prev => {
                 const newCategories = [...prev];
                 const parent = findParentCategoryInTree(newCategories, categoryId);
                 if (parent && parent.children) {
                   const child = parent.children[fromIndex];
                   parent.children?.splice(fromIndex, 1);
                   parent.children?.splice(toIndex, 0, child);
                 }
                 return newCategories;
               });
               break;
        }
        case 'deleteContent': {
          const contents = Array.isArray(operation.data) ? operation.data : [operation.data];
          for (const { content } of contents) {
            await api.delete('/content', { params: { contentId: content.id } });
          }
          setCategories(prev => {
            const newCategories = [...prev];
            const category = findCategoryInTree(newCategories, contents[0].categoryId);
            if (category) {
              category.contents = category.contents?.filter(
                c => !contents.some(selected => selected.content.id === c.id)
              );
            }
            return newCategories;
          });
          break;
        }
        case 'addContent': {
          const { content, categoryId } = operation.data;
          const response = await api.post('/content', {
            parentId: categoryId,
            subcase: content.subcase,
            text: content.text,
            id: content.id
          });
          if (!response.data) throw new Error('Failed to create category');
          const newContent = response.data;
          setCategories(prev => {
            const newCategories = [...prev];
            const parent = findCategoryInTree(newCategories, categoryId);
            if (parent) {
             parent.contents = [...(parent.contents || []), newContent];
            }
            return newCategories;
          });
          setSelectedCategory(selectedCategory);
          break;
        }
        case 'pasteContent':
        case 'dropContent': {
          const contents = Array.isArray(operation.data) ? operation.data : [operation.data];
          if ( operation.isBatch) {
            for (const { content, oldContentId, oldParentId, oldIndex } of contents) {
                await api.delete('/content', { params: { contentId: oldContentId } });

                setCategories(prev => {
                  const newCategories = [...prev];
                  const parent = findCategoryInTree(newCategories, oldParentId);
                  if (parent) {
                    parent.contents = parent.contents?.filter(child => child.id !== oldContentId);
                  }
                  return newCategories;
                });
              }
          }

         const newContents: Content[] = [];
         for (const { content, categoryId, index } of contents) {
            const response = await api.post('/content', {
               parentId: content.parentId,
               text: content.text,
               subcase: content.subcase,
               id: content.id
             });
             if (!response.data) throw new Error('Failed to create content');
             newContents.push(response.data);
         }

          setCategories(prev => {
              const newCategories = [...prev];
              const parent = findCategoryInTree(newCategories, contents[0].content.parentId);
              if (parent) {
                parent.contents = [...(parent.contents || []), ...newContents];
              }
              return newCategories;
            });

          const newParent = findCategoryInTree(categories, newContents[0].parentId);
          setSelectedCategory(newParent);
          break;
        }
        case 'updateContent': {
          const { content, prevContent, categoryId } = operation.data;
          const response = await api.post('/content', {
            parentId: categoryId,
            text: content.text,
            subcase: content.subcase,
            id: content.id
          });

           setCategories(prev => {
              const newCategories = [...prev];
              const parent = findCategoryInTree(newCategories, categoryId);
              if (parent && parent.contents) {
                const index = parent.contents.findIndex(child => child.id === content.id);
                parent.contents[index] = content;
              }
              return newCategories;
            });
           break;
        }
        case 'moveContentUp': {
          const { parentId, firstIndex, lastIndex, direction } = operation.data
          await api.put('/categories/movechild', {
              categoryId: parentId,
              firstIndex: firstIndex,
              lastIndex: lastIndex,
              direction: "up"
            },
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            });

            setCategories(prev => {
                const newCategories = [...prev];
                const parent = findCategoryInTree(newCategories, parentId);
                if (parent && typeof parent.contents !== 'undefined') {
                  for (let i = firstIndex; i <= lastIndex; i++) {
                       [parent.contents[i - 1], parent.contents[i]] = [parent.contents[i], parent.contents[i - 1]];
                   }
                }
                return newCategories;
              });
            break;
        }
        case 'moveContentDown': {
          const { parentId, firstIndex, lastIndex, direction } = operation.data
          await api.put('/categories/movechild', {
              categoryId: parentId,
              firstIndex: firstIndex,
              lastIndex: lastIndex,
              direction: "down"
            },
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            });

            setCategories(prev => {
                const newCategories = [...prev];
                const parent = findCategoryInTree(newCategories, parentId);
                if (parent && typeof parent.contents !== 'undefined') {
                  for (let i = lastIndex; i >= firstIndex; i--) {
                     [parent.contents[i], parent.contents[i + 1]] = [parent.contents[i + 1], parent.contents[i]];
                 }
                }
                return newCategories;
              });
            break;
        }
        case 'dragCategoryTo':
        case 'dragContentTo': {
              const { parentId, firstIndex, lastIndex, toIndex } = operation.data
              await api.put('/categories/movechildto', {
                  categoryId: parentId,
                  firstIndex: firstIndex,
                  lastIndex: lastIndex,
                  moveToIndex: toIndex
                },
                {
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                  }
                });

                const updatedCategory = await fetchCategoryWithChildren(parentId);

                setCategories(prevCategories => {
                   const updatedCategories = [...prevCategories];
                   return updateCategoryInTree(updatedCategories, parentId, () => updatedCategory);
                 });

                 setTimeout(() => {
                      setSelectedCategory(updatedCategory);
                      //setSelectedCategories([]);
                      setExpandedNodes(prev => [...prev, updatedCategory.id]);
                    }, 0);
                break;
            }
            case 'renameSubcase': {
                 const { oldName, newName } = operation.data;
                 setLoading(true);
                 const response = await api.put('/documents/subcases/rename', {
                    oldSubcase: oldName,
                    newSubcase: newName
                  }, { params: { documentId: activeDocument?.id } });

                 setLoading(false);
                 if (response.status === 200) {
                      // Update the document's subcases list

                    if (activeDocument) {
                        await handleOpenDocument(activeDocument);
                        activeDocument.subcases.forEach((item, i) => { if (item == oldName) activeDocument.subcases[i] = newName; });
                        Array.from(new Set(activeDocument.subcases));
                    }
                  }
                break;
            }
      }
    } catch (error) {
      console.error('Error redoing operation:', error);
      alert('Failed to redo operation');
    }
    setLoading(false);
  };

  // Add keyboard shortcuts at the top level
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if the target is an input or textarea to avoid interfering with text input
      if (e.target instanceof HTMLInputElement && e.target.type !== "checkbox" || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Check for Ctrl/Cmd + key combinations
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'z':
            if (e.shiftKey) {
              // Ctrl/Cmd + Shift + Z for redo
              e.preventDefault();
              if (canRedo) handleRedo();
            } else {
              // Ctrl/Cmd + Z for undo
              e.preventDefault();
              if (canUndo) handleUndo();
            }
            break;
          case 'x':
            // Ctrl/Cmd + X for cut
            e.preventDefault();
            handleCutCategory();
            break;
          case 'c':
            // Ctrl/Cmd + C for copy
            e.preventDefault();
            handleCopyCategory();
            break;
          case 'v':
            // Ctrl/Cmd + V for paste
            e.preventDefault();
            handlePasteCategory();
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [canUndo, canRedo, selectedCategory, clipboardCategory, clipboardContents, isCutOperation, selectedContents]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current || !resizerRef.current) return;

      const container = resizerRef.current.parentElement;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const newPosition = ((e.clientX - containerRect.left) / containerRect.width) * 100;

      // Limit the split position between 20% and 80% of the container width
      const limitedPosition = Math.min(Math.max(newPosition, 20), 80);
      setSplitPosition(limitedPosition);
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };

    if (isDraggingRef.current) {
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };
  }, []);

  useEffect(() => {
    const handleRightPaneMouseMove = (e: MouseEvent) => {
      if (!isRightPaneDraggingRef.current || !rightPaneResizerRef.current) return;

      const container = rightPaneResizerRef.current.parentElement;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const newPosition = ((e.clientY - containerRect.top) / containerRect.height) * 100;

      // Limit the split position between 30% and 90% of the container height
      const limitedPosition = Math.min(Math.max(newPosition, 30), 90);
      setRightPaneSplitPosition(limitedPosition);
    };

    const handleRightPaneMouseUp = () => {
      isRightPaneDraggingRef.current = false;
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };

    if (isRightPaneDraggingRef.current) {
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleRightPaneMouseMove);
      document.addEventListener('mouseup', handleRightPaneMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleRightPaneMouseMove);
      document.removeEventListener('mouseup', handleRightPaneMouseUp);
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };
  }, []);

  const handleResizerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isDraggingRef.current = true;
  };

  const handleTabChange = async (event: React.SyntheticEvent, newValue: string) => {
    const performTabChange = async () => {
    // Save current document's expanded state before switching
    if (currentDocument && expandedNodes.length > 0) {
      setDocumentExpandedStates(prev => ({
        ...prev,
        [currentDocument.id]: [...expandedNodes]
      }));
    }
    
    setCurrentTab(newValue);
    const document = documents.find(doc => doc.id === newValue);
    if ( document && document.categoryId ) {
     try {
          // Fetch the category tree for this document
          setLoading(true);
          const rootCategory = await fetchCategoryWithChildren(document.categoryId);
            if (!rootCategory) {
              throw new Error('Could not fetch root category');
            }

          setLoading(false);
          // Add the new tab and set it as current
          setCategories([rootCategory]);
          setSelectedCategory(rootCategory);
            setSelectedCategories([]);
            setSelectedContents([]);
            setCurrentContent(null);
        setCurrentDocument(document);
      setActiveDocument(document);
        setActiveDocument(document);
        historyService.setCurrentDocument(document.id);
        
        // Restore expanded state for this document, or default to root category
        const savedExpandedState = documentExpandedStates[document.id];
        if (savedExpandedState && savedExpandedState.length > 0) {
          setExpandedNodes(savedExpandedState);
        } else {
          setExpandedNodes([rootCategory.id]);
        }
         }
         catch (error) {
            console.error('Error switching tabs:', error);
            setLoading(false);
         }
      }
    };

    checkForUnsavedChanges(
      performTabChange,
      'The content editor is currently open with unsaved changes.'
    );
  };

  const handleCloseTab = (event: React.MouseEvent, tabId: string) => {
    event.stopPropagation();
    const doc = documents.find(doc => doc.id === tabId);
    if ( doc ) {
       handleCloseDocument(doc);
    }
  };

  const handleCloseDocument = (document: Document) => {
    const existingTab = tabs.find(tab => tab.label === document.name);
    if (existingTab) {
      const closedTabIndex = tabs.findIndex(tab => tab.id === existingTab.id);
      const newTabs = tabs.filter(tab => tab.id !== existingTab.id);

      // Update the document's open state
      const doc = documents.find(doc => doc.id === existingTab.id);
      if (doc) {
        doc.hasOpenTab = false;
      }

      setTabs(newTabs);

      // Only update currentTab if the closed tab was the current one
      if (tabs.length > 0 && currentTab === existingTab.id) {
        let newCurrentTabId: string | null = null;
        if (newTabs.length > 0) {
          if (closedTabIndex > 0) {
            newCurrentTabId = newTabs[closedTabIndex - 1].id;
          } else {
            newCurrentTabId = newTabs[0].id;
          }
        }
        setCurrentTab(newCurrentTabId);
        const newCurrentDocument = documents.find(doc => doc.id === newCurrentTabId);
        setCurrentDocument(newCurrentDocument);
        setActiveDocument(newCurrentDocument);
        if (!newCurrentDocument) return;
        // Just switch the current document in the history service
        historyService.setCurrentDocument(newCurrentDocument.id);
        handleOpenDocument(newCurrentDocument);
      }

      setSelectedCategory(null);
      setSelectedCategories([]);
      setSelectedContents([]);
    }
  };

  const handleNewDocument = async (name: string, open: boolean):Promise<Document> => {
      try {
          setLoading(true);
          const newDocument = await createDocument(name);
          setLoading(false);
          // Create a new tab for the document
          const newTab: Tab = {
              id: newDocument.name,
              label: name,
              content: (
                  <Typography>
                      Content for {name}
                  </Typography>
              ),
          };
          // Add the new tab
          setTabs(prevTabs => [...prevTabs, newTab]);
          // Set the current tab and document
          setCurrentTab(newDocument.name);
          setCurrentDocument(newDocument);
          setActiveDocument(newDocument);
          if (open)
            await handleOpenDocument(newDocument);
          return newDocument;
      } catch (error) {
          console.error('Error creating document:', error);
          alert('Failed to create document');
          throw error;
      }
  };
  const handleDeleteDocument = async (documentId: string) => {
    try {
      const doc = documents.find(doc => doc.id === documentId)
      setLoading(true);
      await deleteDocument(documentId);

      // Clear the document's history
      historyService.clearDocumentHistory(documentId);

      // If the deleted document was the current one, set history to null
      if (activeDocument?.id === documentId) {
        historyService.setCurrentDocument(null);
      }

      if ( currentDocument?.id == documentId ) {
          setCurrentDocument(undefined);
      }
      setLoading(false);
      if (doc)
        handleCloseDocument(doc);
      // Remove the document from tabs list
    } catch (error) {
      setLoading(false);
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    }
  };

  const handleRenameDocument = async (documentId: string, newName: string) => {
    try {
      setLoading(true);
      
      // Preserve the current document's subcases before renaming
      const currentDoc = currentDocument;
      const existingSubcases = currentDoc?.subcases || [];
      
      const updatedDoc = await updateDocument(documentId, { name: newName });
      setLoading(false);

      // Update the document name in tabs list
    setTabs(tabs.map(tab =>
      tab.id === documentId ? { ...tab, label: newName, id: newName } : tab
    ));

      // If this is the current document, update its state and reopen it
      if (currentDocument && currentDocument.id === documentId) {
        const renamedDoc = { 
          ...updatedDoc, 
          id: newName, 
          name: newName,
          subcases: existingSubcases // Preserve the existing subcases
        };
        setCurrentDocument(renamedDoc);
        setCurrentTab(newName);
        await handleOpenDocument(renamedDoc);
    }
    } catch (error) {
      setLoading(false);
      console.error('Error renaming document:', error);
      alert('Failed to rename document');
    }
  };

  const normalize = (str: string) =>
    str.replace(/[\u200B-\u200D\uFEFF]/g, '').trim();

  const handleLoadFromFile = async (fileName: string, file?: File) => {
    try {
      // Find the file input element
      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
      if (!fileInput || (!file && (!fileInput.files || fileInput.files.length === 0))) {
        throw new Error('No file selected');
      }

      const fileToUpload = file || fileInput.files![0];

      // Create FormData and append the file
      const formData = new FormData();
      formData.append('file', fileToUpload);
      formData.append('customName', fileName);

      // Send the file to the server
      setLoading(true);
      const response = await api.post('/documents/load-from-file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setLoading(false);
      // Open the newly loaded document
      const newDocument = response.data;
       // Fetch subcases for the loaded document before opening it
        if (newDocument.categoryId) {
          const response = await api.get('/documents/subcases', { params: { documentId: newDocument.categoryId} });
          newDocument.subcases = response.data;
        }
      if (newDocument) {
        newDocument.id = normalize(newDocument.name);
        newDocument.name = normalize(newDocument.name);
        newDocument.label = normalize(newDocument.name);
        addDocument(newDocument);

        const newTab: Tab = {
          id: newDocument.name,
          label: newDocument.name,
          content: (
            <Typography>
              Content for {newDocument.name}
            </Typography>
          ),
        };
        // Add the new tab
        setTabs(prevTabs => [...prevTabs, newTab]);
        // Set the current tab and document
        setCurrentTab(newDocument.name);
        setCurrentDocument(newDocument);
        await handleOpenDocument(newDocument);
      }
    } catch (error) {
      setLoading(false);
      console.error('Error loading document from file:', error);
      alert('Failed to load document from file');
    }
  };

  const handleExportDocument = async (documentId: string) => {
    try {
      setLoading(true);
      
      // Get the document to get its name
      const doc = documents.find(doc => doc.id === documentId);
      if (!doc) {
        throw new Error('Document not found');
      }

      // Call the export endpoint
      const response = await api.get('/documents/export', {
        params: { documentId },
        responseType: 'blob'
      });

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${doc.name}.nbr`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error exporting document:', error);
      alert('Failed to export document');
    }
  };

  const getActiveDocument = (): Document | undefined => {
    // Find the document that corresponds to the currently active tab
    const activeTab = tabs.find(tab => tab.id === currentTab);
    if (!activeTab) return undefined;

    // Find the document with the same name as the active tab
    const document = documents.find(doc => doc.name === activeTab.label);
    if (document) {
        return document;
    }
    return undefined;
  };

  const handleDuplicateDocument = async (documentId: string, newName: string, subcases: string[] ) => {
    try {
      setLoading(true);
      const newDocument = await duplicateDocument(documentId, newName, subcases);

      // Fetch subcases for the new document before opening it
        const response = await api.get('/documents/subcases', { params: { documentId: newDocument.categoryId } });
        newDocument.subcases = response.data;

      setLoading(false);
    const newTab: Tab = {
            id: newDocument.name,
            label: newName,
      content: (
        <Typography>
                    Content for {newName}
        </Typography>
      ),
    };

        // Add the new tab
        setTabs(prevTabs => [...prevTabs, newTab]);
        // Set the current tab and document
        setCurrentTab(newDocument.name);
        setCurrentDocument(newDocument);
        await handleOpenDocument(newDocument);
    } catch (error) {
      setLoading(false);
      console.error('Error duplicating document:', error);
      alert('Failed to duplicate document');
    }
  };

  const addNewTab = (documentName: string, document?: Document) => {
    try {
      // Check if document already exists in tabs
      const existingTab = tabs.find(tab => tab.label === documentName);
      if (existingTab) {
        setCurrentTab(existingTab.id);
        setActiveDocument(activeDocument);
        return;
      }

      // If a document is provided, use it directly
      if (document) {
        const newTab: Tab = {
          id: document.id,
          label: document.name,
          content: (
            <Typography>
              Content for {documentName}
            </Typography>
          ),
        };
        setTabs(prevTabs => [...prevTabs, newTab]);
        setCurrentTab(document.id);
        setCurrentDocument(document);
      setActiveDocument(document);
        setActiveDocument(activeDocument);
        document.hasOpenTab = true;
        return;
      }

      // Otherwise, check if document exists in documents but not in tabs
      const existingDocument = documents.find(doc => doc.id === documentName || doc.name === documentName);
      if (existingDocument) {
        const newTab: Tab = {
          id: existingDocument.id,
          label: existingDocument.name,
          content: (
            <Typography>
              Content for {documentName}
            </Typography>
          ),
        };
        setTabs(prevTabs => [...prevTabs, newTab]);
        setCurrentTab(existingDocument.id);
        setCurrentDocument(existingDocument);
        setActiveDocument(getActiveDocument());
        existingDocument.hasOpenTab = true;
        return;
      }
    } catch (error) {
      console.error('Error adding new tab:', error);
    }
  };



  const handleSelectDocument = async (document?: Document) => {
   const performDocumentSelect = async () => {
    if (!document) {
      setCurrentDocument(undefined);
      return;
    }
    if (!document || !document.categoryId) {
      throw new Error('Document not found');
    }

    const existingTab = tabs.find(tab => tab.id === document.id);

    // If the document is already open in a tab, switch to it
    if (existingTab && existingTab.id !== currentTab) {
      try {
        setLoading(true);
        // Set the current document in the history service
        historyService.setCurrentDocument(document.id);

        // Fetch the category tree for this document
        const rootCategory = await fetchCategoryWithChildren(document.categoryId);
        if (!rootCategory) {
          throw new Error('Could not fetch root category');
        }

        setLoading(false);
        // Add the new tab and set it as current
        setCategories([rootCategory]);
        setSelectedCategory(rootCategory);
          setSelectedCategories([]);
          setSelectedContents([]);
          setCurrentContent(null);
          setCurrentTab(existingTab.id);
          setCurrentDocument(document);
      setActiveDocument(document);
        // Restore expanded state for this document, or default to root category
        const savedExpandedState = documentExpandedStates[document.id];
        if (savedExpandedState && savedExpandedState.length > 0) {
          setExpandedNodes(savedExpandedState);
        } else {
          setExpandedNodes([rootCategory.id]);
        }
      } catch (error) {
          console.error('Error selecting document:', error);
        setLoading(false);
        }
    } else {
      // For closed documents, just set the current document without opening it
      // This allows menu operations to work on selected closed documents
      setCurrentDocument(document);
    }
  };

    checkForUnsavedChanges(
      performDocumentSelect,
      'The content editor is currently open with unsaved changes.'
    );
  };

  const handleOpenDocument = async (document: Document) => {
    const performOpenDocument = async () => {
    try {
      if (!document || !document.categoryId) {
        console.error('Document not found:', document.name);
        return;
      }

      // Check if the document is already open in a tab before creating a new one
      const existingTab = tabs.find(tab => tab.id === document.id || tab.label === document.name);
      
      if (!existingTab) {
        // Only create a new tab if one doesn't already exist
        await addNewTab(document.name);
        document.hasOpenTab = true;
      }

      setLoading(true);
      // Fetch the category tree for this document
      const rootCategory = await fetchCategoryWithChildren(document.categoryId);
        if (!rootCategory) {
          throw new Error('Could not fetch root category');
        }
      setLoading(false);
      
      // Set the document content in the UI
      setCategories([rootCategory]);
      setSelectedCategory(rootCategory);
        setSelectedCategories([]);
        setSelectedContents([]);
        setCurrentContent(null);
        setCurrentTab(document.id);
        setCurrentDocument(document);
      setActiveDocument(document);
        historyService.setCurrentDocument(document.id);
        // Restore expanded state for this document, or default to root category
        const savedExpandedState = documentExpandedStates[document.id];
        if (savedExpandedState && savedExpandedState.length > 0) {
          setExpandedNodes(savedExpandedState);
        } else {
          setExpandedNodes([rootCategory.id]);
        }
    } catch (error) {
      console.error('Error opening document:', error);
        setLoading(false);
      alert('Failed to open document');
    }
    };

    checkForUnsavedChanges(
      performOpenDocument,
      'The content editor is currently open with unsaved changes.'
    );
  };

  const handleCategorySelect = async (category: Category | null) => {
    const performCategorySelect = async () => {
    if (category !== selectedCategory) {
      setSelectedCategory(category);
      setSelectedCategories([]);
      setSelectedContents([]);
      setCurrentContent(null);
    }
    };

    checkForUnsavedChanges(
      performCategorySelect,
      'The content editor is currently open with unsaved changes.'
    );
  };

  const handleCategoryClick = async (category: Category) => {
    try {
      const updatedCategory = await fetchCategoryWithChildren(category.id);
      handleCategorySelect(updatedCategory);
    } catch (error) {
      console.error('Error fetching category:', error);
    }
  };

  const handleCategoryMultiSelect = (category: Category, event: React.MouseEvent) => {
    if (event.ctrlKey || event.metaKey) {
      setSelectedCategories(prev => {
        const isSelected = prev.some(c => c.id === category.id);
        if (isSelected) {
          return prev.filter(c => c.id !== category.id);
        } else {
          return [...prev, category];
        }
      });
    }
    else if (event.shiftKey) {
      if (!category || !category.parentId) return;
      const parent = findCategoryInTree(categories, category.parentId);
      if (!parent || !parent.children) return;
      const siblings = parent.children.map(c => c.id);
      const selectedIds = selectedCategories.map(c => c.id);
      // Find the indices of the selected categories
      const indices = selectedIds.map(id => siblings.indexOf(id)).sort((a, b) => a - b);
      const categoryIndex = siblings.indexOf(category.id);

      let closest = -1;
      for (let i = indices.length - 1; i >= 0; i--) {
        closest = indices[i];
        if (indices[i] < categoryIndex) break;
      }

      setSelectedCategories(prev => {
        return parent.children!.slice(Math.min(closest, categoryIndex), Math.max(closest, categoryIndex) + 1);
      });
    }
    else {
      setSelectedCategories(prev => {
        const isSelected = prev.some(c => c.id === category.id);
        if (isSelected) {
          return [];
        } else {
          return [category];
        }
      });
    }
  };

  const handleContentMultiSelect = (content: Content, event: React.MouseEvent) => {
    if (event.ctrlKey || event.metaKey) {
      setCurrentContent(null);
      setSelectedContents(prev => {
        const isSelected = prev.some(c => c.id === content.id);
        if (isSelected) {
          return prev.filter(c => c.id !== content.id);
        } else {
          return [...prev, content];
        }
      });
    }
    else
    if (event.shiftKey) {
         if (!content || !content.parentId) return;
         const parent = findCategoryInTree(categories, content.parentId);
         if (!parent || !parent.contents) return;
         const siblings = parent.contents.map(c => c.id);
         const selectedIds = selectedContents.map(c => c.id);
               // Find the indices of the selected categories
         const indices = selectedIds.map(id => siblings.indexOf(id)).sort((a, b) => a - b);
         const contentIndex = siblings.indexOf(content.id);

         let closest = -1;
         for ( let i = indices.length - 1; i >= 0; i-- ) {
            closest = indices[i]
            if ( indices[i] < contentIndex )
                break;
         }

         setSelectedContents(prev => {
            return parent.contents!.slice(Math.min(closest, contentIndex), Math.max(closest, contentIndex) + 1);
         });
    } else {
      setSelectedContents(prev => {
          const isSelected = prev.some(c => c.id === content.id);
          if (isSelected) {
            setCurrentContent(null);
            return [];
          } else {
            setCurrentContent(content);
            return [content];
          }
        });
    }

  };

  const handleSelectAllCategories = (checked: boolean) => {
      if (checked) {
        setSelectedCategories(selectedCategory?.children ?? []);
      } else {
        setSelectedCategories([]);
      }
    };


  const handleSelectAllContents = (checked: boolean) => {
    if (checked) {
      setCurrentContent(null);
      setSelectedContents(selectedCategory?.contents ?? []);
    } else {
      setSelectedContents([]);
    }
  };

  const handleCategoriesChange = (updatedCategories: Category[]) => {
    setCategories(updatedCategories);
  };

  const isDuplicateName = (name: string, siblings: Category[]): boolean => {
    return siblings.some(cat => cat.name.toLowerCase() === name.toLowerCase());
  };

  const handleNewCategoryNameChange = (newName: string) => {
    if ( !selectedCategory || !selectedCategory.children ) return;
    if (!newName.trim()) {
       setAddError('Category name is required');
    } else if ( isDuplicateName(newName, selectedCategory.children) ) {
        setAddError('A category with this name already exists');
    } else if ( newName.toLowerCase() === selectedCategory.name.toLowerCase() ) {
        setAddError('Name must be different than the parent category');
    }else {
        setAddError('');
    }
    setNewName(newName);
  }

  const getNonSelectedCategories = (category: Category, selectedCategories: Category[]): Category[] => {
    if (!category.children) return [];
    return category.children.filter(child => !selectedCategories.some(selected => selected.id === child.id));
  };

  const handleMergeCategoriesNameChange = (newName: string) => {
    if (!selectedCategory || !selectedCategory.children) return;
    if (!newName.trim()) {
      setAddError('Category name is required');
    } else if (isDuplicateName(newName, getNonSelectedCategories(selectedCategory, selectedCategories))) {
      setAddError('A category with this name already exists');
    } else if (newName.toLowerCase() === selectedCategory.name.toLowerCase()) {
      setAddError('Name must be different than the parent category');
    } else {
      setAddError('');
    }
    setNewName(newName);
  };

  const handleRenameCategoryNameChange = (newName: string) => {
      if ( !selectedCategory || !selectedCategory.parentId ) return;
      const parent = findCategoryInTree(categories, selectedCategory.parentId);
      if (!parent) return;
      if (!newName.trim()) {
          setRenameError('Category name is required');
      }
      else if ( newName.toLowerCase() !== selectedCategory.name.toLowerCase() && isDuplicateName(newName, getSiblings(categories, selectedCategory.id)) ) {
          setRenameError('A category with this name already exists');
      } else if ( newName.toLowerCase() === parent.name.toLowerCase() ) {
          setRenameError('Name must be different than the parent category');
      } else {
          setRenameError('');
      }
      setNewName(newName);
    }
  const handleRenameCategory = () => {
    // If there are selected categories in the right pane, use those
    const categoryToRename = selectedCategories.length > 0 ? selectedCategories[0] : selectedCategory;

    if (!categoryToRename || !canRenameCategory(categoryToRename)) return;
    setNewName(categoryToRename.name);
    setNewDescription(categoryToRename.description);
    setRenameDialogOpen(true);
  };

  const handleRenameDialogClose = () => {
    setRenameDialogOpen(false);
    setNewName('');
    setNewDescription('');
    setRenameError('');
  };

  const handleRenameDialogConfirm = async () => {
    if (!selectedCategory || !newName) return;

    try {
      // If the new name is the same as the current name, just close the dialog
      if (newName.toLowerCase() === selectedCategory.name.toLowerCase() &&
          newDescription.toLowerCase() === selectedCategory.description.toLowerCase()) {
        setRenameDialogOpen(false);
        setNewName('');
        setNewDescription('');
        return;
      }

      // Proceed with the rename
      const response = await api.put('/categories', {
            parentId: selectedCategory.id,
            name: newName,
            description: newDescription
        }
      );

      const renamedCategory = response.data;

       historyService.recordOperation({
          type: 'renameCategory',
          data: {
            category: renamedCategory,
            oldId: selectedCategory.id,
            oldName: selectedCategory.name,
            oldDescription: selectedCategory.description,
          }
        });

      // Close the dialog
      setRenameDialogOpen(false);
      setNewName('');
      setNewDescription('');
      setRenameError('');


      // Fetch the complete category tree starting from the root
      const updatedCategory = await fetchCategoryWithChildren(renamedCategory.parentId);
      if (!updatedCategory) {
        throw new Error('Could not fetch root category');
      }

      setCategories(prevCategories => {
          const updatedCategories = [...prevCategories];
          return updateCategoryInTree(updatedCategories, updatedCategory.id, () => updatedCategory);
        });
        const cat = findCategoryInTree(categories, renamedCategory.id);
        if (cat) {
            setTimeout(() => {
                 setSelectedCategory(cat);
                 //setSelectedCategories([]);
                 setExpandedNodes(prev => [...prev, cat.id]);
               }, 0);
         }
    } catch (error) {
      console.error('Error renaming category:', error);
      alert('Failed to rename category: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const findParentCategoryInTree = (categories: Category[], targetId: string): Category | null => {
    for (const category of categories) {
      // Check if this category has the target as a child in the children array
      if (category.children && category.children.some(child => child.id === targetId)) {
        return category;
      }

      if (category.contents && category.contents.some(child => child.id === targetId)) {
         return category;
      }

      // Recursively check children
      if (category.children && category.children.length > 0) {
        const found = findParentCategoryInTree(category.children, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  const canMoveCategory = (category: Category | null, direction: 'up' | 'down'): boolean => {
    if (!category) return false;
    if (category.parentId === null) return false;

    // For nested categories, find the parent
    const findParent = (cats: Category[], targetId: string): Category | null => {
        for (const cat of cats) {
        if (!cat) continue;
        if (cat.children && Array.isArray(cat.children)) {
          if (cat.children.some(child => child.id === targetId)) {
          return cat;
        }
          const found = findParent(cat.children, targetId);
            if (found) return found;
          }
        }
        return null;
      };

    const parent = findParent(categories, category.id);
    if (!parent || !parent.children || !Array.isArray(parent.children)) return false;

    const index = parent.children.findIndex((child: Category) => child.id === category.id);
    if (direction === 'up' && index === 0) return false;
    if (direction === 'down' && index === parent.children.length - 1) return false;

    return true;
  };

  const handleMoveCategory = async (direction: 'up' | 'down') => {
    if (!canMoveSelectedCategories(direction)) return;

    if ( selectedContents.length > 0 )
        return handleMoveContent(direction);
    // If there are selected categories in the right pane, use those
    const categoriesToMove = selectedCategories.length > 0 ? selectedCategories :
      (selectedCategory ? [selectedCategory] : []);


    try {
      const parentId = categoriesToMove[0].parentId;
      if (!parentId) return;

      const parent = findParentCategoryInTree(categories, categoriesToMove[0].id);
      if (!parent || !parent.children) return;

      const allIds = [...parent.children].map(c => c.id);
      const selectedIds = categoriesToMove.map(c => c.id);

      // Find the indices of the selected categories
      const indices = selectedIds.map(id => allIds.indexOf(id)).sort((a, b) => a - b);
       // Update the backend with the new order
       await api.put('/categories/movechild', {
              categoryId: parentId,
              firstIndex: indices[0],
              lastIndex: indices[indices.length - 1],
              direction: direction
            },
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            });

      // Move the categories
      if (direction === 'up') {
        historyService.recordOperation({
            type: 'moveCategoryUp',
            data: {
              parentId: parentId,
              firstIndex: indices[0],
              lastIndex: indices[indices.length - 1],
              direction: direction
            }
          });

        // Move up by swapping with the previous category
        indices.forEach(index => {
          if (index > 0) {
            [allIds[index], allIds[index - 1]] = [allIds[index - 1], allIds[index]];
          }
        });



      } else {

        historyService.recordOperation({
            type: 'moveCategoryDown',
            data: {
              parentId: parentId,
              firstIndex: indices[0],
              lastIndex: indices[indices.length - 1],
              direction: direction
            }
          });

        // Move down by swapping with the next category
        indices.reverse().forEach(index => {
          if (index < allIds.length - 1) {
            [allIds[index], allIds[index + 1]] = [allIds[index + 1], allIds[index]];
          }
        });

      }

      // Update the categories state with the new tree
      setCategories(prevCategories => {
        const updatedCategories = [...prevCategories];

        // Create the updated parent with new children order
        const updatedParent = {
          ...parent,
          children: allIds
            .map(id => parent.children?.find(child => child.id === id))
            .filter((child): child is Category => child !== undefined)
        };

        // Update the tree with the modified parent
        const newCategories = updateCategoryInTree(updatedCategories, parent.id, () => updatedParent);

        // Find the moved categories in their new positions
        const findCategoryInTree = (cats: Category[], targetId: string): Category | null => {
          for (const cat of cats) {
            if (cat.id === targetId) return cat;
          if (cat.children) {
              const found = findCategoryInTree(cat.children, targetId);
            if (found) return found;
          }
        }
        return null;
      };

        // Get the updated categories with their new positions
        const updatedCategoriesToSelect = categoriesToMove.map(cat =>
          findCategoryInTree(newCategories, cat.id)
        ).filter((cat): cat is Category => cat !== null);

        // Schedule the selection updates after the state update
        setTimeout(() => {
          if (selectedCategories.length > 0) {
            setSelectedCategories(updatedCategoriesToSelect);
            setSelectedCategory(updatedParent);
          } else {
            setSelectedCategory(updatedCategoriesToSelect[0]);
          }
        }, 0);

        return newCategories;
      });
    } catch (error) {
      console.error('Error moving categories:', error);
      alert('Failed to move categories: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleMoveContent = async (direction: 'up' | 'down') => {
      const contentToMove = selectedContents;

      try {
        const parentId = contentToMove[0].parentId;
        if (!parentId) return;

        const parent = findParentCategoryInTree(categories, contentToMove[0].id);
        if (!parent || !parent.contents) return;

        const allIds = [...parent.contents].map(c => c.id);
        const selectedIds = contentToMove.map(c => c.id);

        // Find the indices of the selected categories
        const indices = selectedIds.map(id => allIds.indexOf(id)).sort((a, b) => a - b);
         // Update the backend with the new order
         await api.put('/categories/movechild', {
                categoryId: parentId,
                firstIndex: indices[0],
                lastIndex: indices[indices.length - 1],
                direction: direction
              },
              {
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded'
                }
              });

         // Move the categories
          if (direction === 'up') {
             historyService.recordOperation({
                type: 'moveContentUp',
                data: {
                  parentId: parentId,
                  firstIndex: indices[0],
                  lastIndex: indices[indices.length - 1],
                  direction: direction
                }
              });
            // Move up by swapping with the previous category
            indices.forEach(index => {
              if (index > 0) {
                [allIds[index], allIds[index - 1]] = [allIds[index - 1], allIds[index]];
              }
            });
          } else {
           historyService.recordOperation({
              type: 'moveContentDown',
              data: {
                parentId: parentId,
                firstIndex: indices[0],
                lastIndex: indices[indices.length - 1],
                direction: direction
              }
            });
            // Move down by swapping with the next category
            indices.reverse().forEach(index => {
              if (index < allIds.length - 1) {
                [allIds[index], allIds[index + 1]] = [allIds[index + 1], allIds[index]];
              }
            });

          }

          // Update the categories state with the new tree
        setCategories(prevCategories => {
          const updatedCategories = [...prevCategories];

          // Create the updated parent with new children order
          const updatedParent = {
            ...parent,
            contents: allIds
              .map(id => parent.contents?.find(child => child.id === id))
              .filter((child): child is Content => child !== undefined)
          };

          // Update the tree with the modified parent
          const newCategories = updateCategoryInTree(updatedCategories, parentId, () => updatedParent);

          // Get the updated categories with their new positions
          const updatedContentToSelect = contentToMove.map(cont =>
            parent.contents?.find(child => child.id === cont.id)
          ).filter((cont): cont is Content => cont !== null);

          // Schedule the selection updates after the state update
          setTimeout(() => {
            if (selectedContents.length > 0) {
              setSelectedContents(updatedContentToSelect);
              setSelectedCategory(updatedParent);
            }
          }, 0);

          return newCategories;
        });
      } catch (error) {
        setLoading(false);
        console.error('Error moving categories:', error);
        alert('Failed to move categories: ' + (error instanceof Error ? error.message : 'Unknown error'));
      }
    };

  const getSiblings = (categories: Category[], categoryId: string): Category[] => {
    const parent = findParentCategoryInTree(categories, categoryId);
    if (!parent) return categories;
    return parent.children || [];
  };

  const updateCategoryName = (categories: Category[], targetId: string, newName: string, newDescription: string): Category[] => {
    return categories.map(cat =>
      cat.id === targetId ? { ...cat, name: newName, description: newDescription } : cat
    );
  };

  const createCategory = async (parentId: string | undefined, name: string, description: string): Promise<Category | null> => {
    try {
      const response = await api.post('/categories', {
        name,
        description,
        parentId: parentId || null,  // Ensure we send null if parentId is undefined
        content: '',  // Add empty content
        childrenIds: [],  // Initialize with empty children array
        children: []  // Initialize with empty children array
      });
      return response.data;
    } catch (error) {
      console.error('Error creating category:', error);
      if (axios.isAxiosError(error)) {
        console.error('Axios error details:', error.response?.data);
      }
      throw error; // Rethrow the error to handle it in the calling function
    }
  };

  const handleAddCategory = async () => {
    if (!selectedCategory || !newName) return;

    try {
      const response = await api.post('/categories', {
        parentId: selectedCategory.id,
        name: newName,
        description: newDescription
      });
      if (!response.data) throw new Error('Failed to create category');
      const newCategory = response.data;
      const parentId = newCategory.parentId;

      // Record the operation
      historyService.recordOperation({
        type: 'addCategory',
        data: {
          category: newCategory,
          parentId: parentId
        }
      });

      setCategories(prev => {
        const newCategories = [...prev];
        const parent = findCategoryInTree(newCategories, parentId);
        if (parent) {
          parent.children = [...(parent.children || []), newCategory];
        }
        return newCategories;
      });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        localStorage.removeItem('token');
        window.location.replace('/login');
        return;
      }
      console.error('Error adding category:', error);
      alert('Failed to add category');
    }
  };

  const handleCutCategory = () => {
    if (!canCutSelectedCategories()) return;

    setClipboardCategory(null);
    setClipboardCContents([]);
    // If there are selected categories in the right pane, use those
    if ( selectedContents.length > 0 ) {
        setClipboardCContents(selectedContents);
    } else {
        const categoryToCut = selectedCategories.length > 0 ? selectedCategories[0] : selectedCategory
        setClipboardCategory(categoryToCut);
    }
    setIsCutOperation(true);
  };

  const handleCopyCategory = () => {
     if (!canCopySelectedCategories()) return;
     setClipboardCategory(null);
     setClipboardCContents([]);
     if ( selectedContents.length > 0 ) {
        setClipboardCContents(selectedContents);
     } else {
    // If there are selected categories in the right pane, use those
         const categoryToCopy = selectedCategories.length > 0 ? selectedCategories[0] : selectedCategory
         setClipboardCategory(categoryToCopy);
    }
    setIsCutOperation(false);
  };

  const handlePasteCategory = async () => {
    if ( clipboardContents.length > 0 )
        handlePasteContents();
    if (!clipboardCategory || !selectedCategory) return;
    setLoading(true);
    var newCategory = clipboardCategory;
    try {
      // Create a new category in the backend
      if (isCutOperation) {
        const response = await api.put('/categories/move', {
          categoryId: clipboardCategory.id,
          newParentId: selectedCategory.id
        },
         {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        });
        newCategory = response.data;

        const parent  = findParentCategoryInTree(categories, clipboardCategory.id);

          // Fetch the complete category tree starting from the root
        const updatedCategory = await fetchCategoryWithChildren(newCategory.id);
        if (!updatedCategory) {
          throw new Error('Could not fetch root category');
        }

        historyService.recordOperation({
            type: 'pasteCategory',
            data: {
              category: updatedCategory,
              oldCategoryId: clipboardCategory.id,
              oldParentId: parent!.id,
              newParentId: updatedCategory.parentId,
              oldIndex: parent!.children!.findIndex(c => c.id === clipboardCategory.id) ?? -1,
              newIndex: -1
            }
          });

        setCategories(prevCategories => {
            let updatedCategories = [...prevCategories];

            // Update the target parent with the new category
            const updatedTargetParent = {
              ...selectedCategory,
              children: [...(selectedCategory.children || []), updatedCategory]
            };

            // Update the tree with the modified target parent
            updatedCategories = updateCategoryInTree(updatedCategories, selectedCategory.id, () => updatedTargetParent);

            // Find and update the original parent to remove the category
            const originalParent = findParentCategoryInTree(updatedCategories, clipboardCategory.id);
            if (originalParent) {
              const updatedOriginalParent = {
                ...originalParent,
                children: (originalParent.children || []).filter(child => child.id !== clipboardCategory.id)
              };
              // Update the tree with the modified original parent
              updatedCategories = updateCategoryInTree(updatedCategories, originalParent.id, () => updatedOriginalParent);
            }

            return updatedCategories;
          });
    } else {
        const response = await api.post('/categories', {
          name: clipboardCategory.name,
          description: clipboardCategory.description,
          parentId: selectedCategory.id
        });
        newCategory = response.data;

        historyService.recordOperation({
            type: 'pasteCategory',
            data: {
              category: newCategory,
              oldParentId: null,
              newParentId: newCategory.parentId,
              oldIndex: -1,
              newIndex: -1
            }
          });

        await addSubTree(newCategory, clipboardCategory);

        setCategories(prevCategories => {
            let updatedCategories = [...prevCategories];

            // Update the target parent with the new category
            const updatedTargetParent = {
              ...selectedCategory,
              children: [...(selectedCategory.children || []), newCategory]
            };

            // Update the tree with the modified target parent
            updatedCategories = updateCategoryInTree(updatedCategories, selectedCategory.id, () => updatedTargetParent);

            return updatedCategories;
          });

      }

       setLoading(false);

      // Clear the clipboard
      setClipboardCategory(null);
      setIsCutOperation(false);
    } catch (error) {
      setLoading(false);
      console.error('Error pasting category:', error);
      alert('Failed to paste category: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const pasteContent = async (content: Content):  Promise<Content | null> => {
    if (!selectedCategory) return null;
    var response;
    setLoading(true);
    if (isCutOperation) {
      response = await api.put(`/content/move`, {
        contentId: content.id,
        newParentId: selectedCategory.id
      },
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
    } else {
      response = await api.post(`/content`, {
        parentId: selectedCategory.id,
        text: content.text,
        subcase: content.subcase
      });
    }
    setLoading(false);
    return response.data;
  };


  const handlePasteContents = async () => {
    if (clipboardContents.length == 0 || !selectedCategory) return;

    const duplicates = clipboardContents.filter(content =>
        hasDuplicateContent(selectedCategory, content.subcase, content.text)
    );

    if (duplicates.length > 0) {
        const duplicateNames = duplicates.map(c => `"${c.subcase}"`).join(', ');
        const message = `The following contents already exist in this category: ${duplicateNames}. Please modify the subcases or choose a different category.`;
        alert(message);
        return;
    }

    let newContents: Content[] = [];
    try {
      const oldParent = findParentCategoryInTree(categories, clipboardContents[0].id);

      const operationData: { content: Content;
        oldContentId: string;
        oldParentId: string;
        oldIndex: number;
       }[] = [];

      for (const content of clipboardContents) {
        const oldIndex = oldParent!.contents!.findIndex(c => c.id === content.id) ?? -1;
        const oldContentId = content.id;

        const newContent = await pasteContent(content);
        if ( !newContent )
            throw new Error('Failed to paste content');
        newContents.push(newContent);
        operationData.push({content: newContent, oldContentId: oldContentId, oldParentId: oldParent!.id, oldIndex: oldIndex});
      };

      historyService.recordOperation({
        type: 'pasteContent',
        data: operationData,
        isBatch: isCutOperation
      });

      // Update the categories state with the new tree
      setCategories(prevCategories => {
        let updatedCategories = [...prevCategories];

        // If it's a cut operation, remove the category from its original location
        if (isCutOperation) {
          const originalParent = findParentCategoryInTree(updatedCategories, clipboardContents[0].id);
          if (originalParent) {
            // Update the original parent to remove the category
            const updatedOriginalParent = {
              ...originalParent,
              contents: (originalParent.contents || []).filter(child => !clipboardContents.includes(child))
            };
            // Update the tree with the modified original parent
            updatedCategories = updateCategoryInTree(updatedCategories, originalParent.id, () => updatedOriginalParent);
          }
        }

        // Update the target parent with the new category
        const updatedTargetParent = {
          ...selectedCategory,
          contents: [...(selectedCategory.contents || []), ...newContents]
        };

        // Update the tree with the modified target parent
        const newCategories = updateCategoryInTree(updatedCategories, selectedCategory.id, () => updatedTargetParent);

        // Select the new category and expand the parent
        setTimeout(() => {
          setSelectedCategory(updatedTargetParent);
          setSelectedCategories([]);
          setExpandedNodes(prev => [...prev, selectedCategory.id]);
        }, 0);

        return newCategories;
      });


      // Clear the clipboard
      setClipboardCContents([]);
      setIsCutOperation(false);
    } catch (error) {
      console.error('Error pasting category:', error);
      alert('Failed to paste category: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const canDeleteCategory = (category: Category | null): boolean => {
    if (!category) return false;

    // Cannot delete root category
    if (!category.parentId) return false;

    // Find parent category in the tree
    const parent = findParentCategoryInTree(categories, category.id);
    if (!parent || !parent.children) return false;

    // Parent must have more than 2 children
    return parent.children.length > 2;
  };

  const canCutCategory = (category: Category | null): boolean => {
    return canDeleteCategory(category);
  };

  const canDelete = () : boolean => {
    if (selectedContents.length > 0)
        return true;

    if ( selectedCategories.length > 0 )
      return false;

    return selectedCategory ? canDeleteCategory(selectedCategory) : false;
  }

  const canDeleteSelectedCategories = (): boolean => {
    if ( selectedContents.length > 0 )
        return true;

    if (selectedCategories.length > 1)
      return false;

    if (selectedCategories.length === 0) {
      // If no categories are selected in the right pane, check the tree selection
      return selectedCategory ? canDeleteCategory(selectedCategory) : false;
    }

    // Get all categories in the current parent
    const parentId = selectedCategories[0].parentId;
    if (!parentId) return false;

    const parent = findCategoryInTree(categories, parentId);
    if (!parent || !parent.children) return false;

    // Parent must have more than 2 children
    return selectedCategories[0].contentCategory && parent.children.length - selectedCategories.length > 1;
  };

  const canCutSelectedCategories = (): boolean => {
    return canDeleteSelectedCategories();
  };

  const canCopyCategory = (category: Category | null): boolean => {
    if (!category) return false;

    // Cannot copy root category
    if (!category.parentId) return false;

    return true;
  };

  const canCopySelectedCategories = (): boolean => {
    if ( selectedContents.length > 0 )
            return true;

    if (selectedCategories.length === 0) {
          // If no categories are selected in the right pane, check the tree selection
          return selectedCategory ? canCopyCategory(selectedCategory) : false;
    }
    return selectedCategories.length < 2;
  };

  const canPaste = (category: Category | null): boolean => {
    if (!category) return false;
    if (selectedCategories.length > 0 || selectedContents.length > 0) return false;

    if ( category.contentCategory )
        return clipboardContents.length > 0 ? true : false;

    return clipboardCategory !== null &&
        category.children!.length >= 2 &&
        category.name !== clipboardCategory.name &&
        category.children !== undefined &&
        !isDuplicateName(clipboardCategory.name, category.children);
  };

  const handleEditDocument = (document: any) => {
    setCurrentTab(document.id);
    setCurrentDocument(documents.find(doc => doc.id === document.id));
  };

  const canAddCategory = (category: Category | null): boolean => {
    if (!category)
      return false;
    return category.contentCategory === false ? category.children!.length >= 2 : false;
  };

  const handleDelete = async () => {
    if (selectedContents.length > 0) {
      handleDeleteContent();
    }
    else if ( selectedCategory ) {
      handleDeleteCategory()
    }
  };

  const handleSplitDialogOpen = () => {
    if (!selectedCategory) return;

    setSplitNames({
      first: `${selectedCategory.name} - 1`,
      second: `${selectedCategory.name} - 2`
    });
    setSplitError('');
    setSplitDialogOpen(true);
  };

  const handleSplitDialogClose = () => {
    setSplitDialogOpen(false);
    setSplitNames({ first: '', second: '' });
    setSplitError('');
  };

  const validateSplitNames = (first: string, second: string) => {
    if (first.trim().toLowerCase() === second.trim().toLowerCase()) {
      setSplitError('The two categories cannot have the same name');
      return false;
    }
    if (
      selectedCategory &&
      (first.trim().toLowerCase() === selectedCategory.name.toLowerCase() ||
       second.trim().toLowerCase() === selectedCategory.name.toLowerCase())
    ) {
      setSplitError('Sub categories cannot have the same name as the parent category');
      return false;
    }
    setSplitError('');
    return true;
  };

  const handleSplitDialogConfirm = async () => {
    if (!selectedCategory || !splitNames.first || !splitNames.second) return;
    if (splitError) return;

    try {
      // Use the selected category's ID as the parent ID for the new categories
      const parentCategoryId = selectedCategory.id;

      let newChildren = [];
      let newChildrenIds = [];
      setLoading(true);
      // Create the first new category with the correct parent ID
      const firstCategory = await createCategory(parentCategoryId, splitNames.first, "");
      if (!firstCategory) throw new Error('Failed to create first category');

      // Create the second new category with the correct parent ID
      const secondCategory = await createCategory(parentCategoryId, splitNames.second, "");
      if (!secondCategory) throw new Error('Failed to create second category');

      newChildren.push(firstCategory, secondCategory);
      newChildrenIds.push(firstCategory.id, secondCategory.id);

      let movedContentsFirst: {content: Content, oldContentId: string, index:number}[]  = [];
      let movedContentsSecond: {content: Content, oldContentId: string, index:number}[]  = [];

      let tempCategory: Category | null = firstCategory;
      var contentsToMoveToFirst = selectedCategory && selectedCategory.contents  ? selectedCategory.contents.filter(c => selectedContents.includes(c)) : [];
      var contentsToMoveToSecond = selectedCategory && selectedCategory.contents  ? selectedCategory.contents.filter(c => !selectedContents.includes(c)) : [];
      if ( selectedCategory.contents && selectedCategory.contents.length > 0 && selectedContents.length === 0) {
          tempCategory = await createCategory(parentCategoryId, "Temp - " + selectedCategory.name, "Temporary category for " + selectedCategory.name + " content");
          if (tempCategory === null) throw new Error('Failed to temporary second category');
          contentsToMoveToFirst = selectedCategory.contents;
          contentsToMoveToSecond = [];
          newChildren.push(tempCategory);
          newChildrenIds.push(tempCategory.id);
      }

      for (const content of contentsToMoveToFirst) {
          const index = selectedCategory.contents!.findIndex(c => c.id === content.id);
          try {
             const response = await api.put(`/content/move`, {
                 contentId: content.id,
                 newParentId: tempCategory?.id
               },
               {
                 headers: {
                   'Content-Type': 'application/x-www-form-urlencoded'
                 }
              });
              const movedContent = response.data;
              movedContentsFirst.push({content: movedContent, oldContentId: content.id, index: index});
          } catch (error) {
              console.error(`Error fetching data for ${content.id}:`, error);
          }
      }

      for (const content of contentsToMoveToSecond) {
        const index = selectedCategory.contents!.findIndex(c => c.id === content.id);
        try {
           const response = await api.put(`/content/move`, {
               contentId: content.id,
               newParentId: secondCategory?.id
             },
             {
               headers: {
                 'Content-Type': 'application/x-www-form-urlencoded'
               }
            });
            const movedContent = response.data;
            movedContentsSecond.push({content: movedContent, oldContentId: content.id, index: index});
        } catch (error) {
            console.error(`Error fetching data for ${content.id}:`, error);
        }
    }

    setLoading(false);

      historyService.recordOperation({
        type: 'splitCategory',
        data: {
           firstCategory,
           secondCategory,
           parentId: parentCategoryId,
           tempCategory,
           movedContentsFirst,
           movedContentsSecond
        },
      });

      // Close the dialog
      setSplitDialogOpen(false);
      setSplitNames({ first: '', second: '' });
      setSplitError('');

      selectedCategory.contentCategory = false;

        const updatedParent = {
          ...selectedCategory,
          children: newChildren
        };

        // Update the categories state with the new tree
        setCategories(prevCategories => {
          const updatedCategories = [...prevCategories];

          // Update the tree with the modified parent
          return updateCategoryInTree(updatedCategories, parentCategoryId, () => updatedParent);
        });


        const updateTemp = {
          ...tempCategory,
          contents: movedContentsFirst.map(c => c.content)
        };

        const tempId = tempCategory.id;
        // Update the categories state with the new tree
        setCategories(prevCategories => {
          const updatedCategories = [...prevCategories];

          // Update the tree with the modified parent
          return updateCategoryInTree(updatedCategories, tempId, () => updateTemp);
        });

        const updateSecond = {
          ...secondCategory,
          contents: movedContentsSecond.map(c => c.content)
        };

        // Update the categories state with the new tree
        setCategories(prevCategories => {
          const updatedCategories = [...prevCategories];

          // Update the tree with the modified parent
          return updateCategoryInTree(updatedCategories, secondCategory.id, () => updateSecond);
        });

        // Select the parent category and expand it
        setSelectedCategory(updatedParent);
        setSelectedCategories([]);
        setExpandedNodes(prev => [...prev, parentCategoryId]);
      } catch (error) {
        setLoading(false);
        console.error('Error splitting category:', error);
        alert('Failed to split category: ' + (error instanceof Error ? error.message : 'Unknown error'));
      }
  };

  const canGroupCategories = (): boolean => {
    if (!selectedCategory || !selectedCategory.children) return false;
    return selectedCategories.length > 1 && selectedCategory.children.length > selectedCategories.length;
  };

  const handleGroupCategoriesDialogOpen = () => {
       if (!canGroupCategories()) return;
      setNewName('');
      setNewDescription('');
      setAddError('');
      setGroupCategoriesDialogOpen(true);
  };

  const handleGroupCategoriesDialogClose = () => {
      setGroupCategoriesDialogOpen(false);
      setNewName('');
      setNewDescription('');
      setAddError('');
  };

    const handleGroupCategoriesDialogConfirm = async () => {
       if (!newName.trim()) {
        setAddError('Category name is required');
        return;
      }
      setGroupCategoriesDialogOpen(false);
      await handleGroupCategories();
  };

  const handleGroupCategories = async () => {
    try {
      setLoading(true);
      if (!selectedCategory) {
        throw new Error('No category selected');
      }

      const response = await api.post('/categories', {
        parentId: selectedCategory.id,
        name: newName,
        description: newDescription
      });
      if (!response.data) throw new Error('Failed to create category');
      const newCategory = response.data;

      const historyCategories: {name: string, index: number}[] = [];
      const movedCategories: Category[] = [];
      for (const category of selectedCategories) {
        const index = selectedCategory.children!.findIndex(c => c.id === category.id);
        const response = await api.put('/categories/move', {
          categoryId: category.id,
          newParentId: newCategory.id
        },
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        });
        const movedCategory = response.data;
        historyCategories.push({name: movedCategory.name, index: index!});
        const categoryWithChildren = await fetchCategoryWithChildren(newCategory.id);
        if (!categoryWithChildren) {
          throw new Error('Could not fetch root category');
        }
        movedCategories.push(categoryWithChildren);
      }

      historyService.recordOperation({
        type: 'groupCategories',
        data: {
          originalParentId: selectedCategory.id,
          newParent: newCategory,
          groupedCategories: historyCategories
        }
      });

      // Fetch the complete updated category tree
      const updatedTree = await fetchCategoryWithChildren(selectedCategory.id);
      if (!updatedTree) {
        throw new Error('Could not fetch updated category tree');
      }

      // Update the state with the complete tree
      setCategories(prev => {
        const newCategories = [...prev];
        const parent = findParentCategoryInTree(newCategories, selectedCategory.id);
        if ( !parent ) return [updatedTree];
        if (parent && parent.children) {
          const index = parent.children.findIndex(cat => cat.id === updatedTree.id);
          if (index !== -1) {
            parent.children[index] = updatedTree;
          } else {
            parent.children.push(updatedTree);
          }
        }
        return newCategories;
      });

      setLoading(false);
    } catch (error) {
      setLoading(false);
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        localStorage.removeItem('token');
        window.location.replace('/login');
        return;
      }
      console.error('Error adding category:', error);
      alert('Failed to add category');
    }
  };

  const canFlattenCategory = (): boolean => {
          if (!selectedCategory || !selectedCategory.parentId || selectedCategory.contentCategory || !selectedCategory?.children) return false;

          const parent = findCategoryInTree(categories, selectedCategory.parentId);
          if (!parent || !parent.children) return false;

          for ( const category of selectedCategory?.children ) {
            if ( category.name === parent.name || isDuplicateName(category.name, parent.children) )
               return false;
          }

          return true;
     };

  const handleFlattenCategory = async () => {
          if (!selectedCategory || !selectedCategory.parentId || !selectedCategory.children) return;

           setLoading(true);

           var flattenCategories: Category[] = [];
            for (const cat of selectedCategory.children) {
                try {
                    const response = await api.put(`/categories/move`, {
                        categoryId: cat.id,
                        newParentId: selectedCategory.parentId
                    },
                    {
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded'
                        }
                    });

                 const movedCat = response.data;
                 const updatedCat = await fetchCategoryWithChildren(movedCat.id);
                 flattenCategories.push(updatedCat)

                } catch (error) {
                    console.error(`Failed to move category ${cat.id}:`, error);
                    throw error;
                }
            }

            await deleteCategory(selectedCategory.id);

            const parent  = findParentCategoryInTree(categories, selectedCategory.id);
           if ( !parent || !parent.children) return;
           const index = parent.children.findIndex(c => c.id === selectedCategory.id);
           const firstIndex = parent.children.length - 1;
           const lastIndex = firstIndex + flattenCategories.length - 1;

           await api.put(`/categories/movechildto`, {
                    categoryId: parent.id,
                    firstIndex,
                    lastIndex,
                    moveToIndex: index
                  },
                  {
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded'
                    }
                  });


          historyService.recordOperation({
            type: 'flattenCategory',
            data: {
                parentId: selectedCategory.parentId,
                category: selectedCategory,
                index: index,
                flattenCategories: flattenCategories,
                originalCategories: selectedCategory.children
            }
          });

            setCategories(prev => {
                const newCategories = [...prev];
                const parent = findParentCategoryInTree(newCategories, selectedCategory.id);
                if (parent && parent.children) {

                    // Remove the selected category from its current position
                    const filteredChildren = parent.children.filter(child => child.id !== selectedCategory.id);

                    // Insert the children at the specific index
                    filteredChildren.splice(index, 0, ...(flattenCategories || []));

                    parent.children = filteredChildren;
                }

                setSelectedCategory(parent);
                return newCategories;
            });

            setLoading(false);


       };

    const canMergeCategories = (): boolean => {
        if (!selectedCategory || !selectedCategory.children) return false;
        if (selectedCategories.length <2 || selectedCategory.children.length === selectedCategories.length) return false;

       for ( const category of selectedCategories ) {
             if ( !category.contentCategory )
                return false;
       }
       return true;
   };

 const handleMergeCategoriesDialogOpen = () => {
       if (!canMergeCategories()) return;
      setNewName('');
      setNewDescription('');
      setAddError('');
      setMergeCategoriesDialogOpen(true);
  };

  const handleMergeCategoriesDialogClose = () => {
      setMergeCategoriesDialogOpen(false);
      setNewName('');
      setNewDescription('');
      setAddError('');
  };

    const handleMergeCategoriesDialogConfirm = async () => {
       if (!newName.trim()) {
        setAddError('Category name is required');
        return;
      }
      setMergeCategoriesDialogOpen(false);
      await handleMergeCategories();
  };

   const handleMergeCategories = async () => {
      if (!selectedCategory) return;
       setLoading(true);

       var mergedCategories: {category: Category, index: number}[] = [];
       for (const category of selectedCategories) {
         const index = selectedCategory.children!.findIndex(c => c.id === category.id);
         await deleteCategory(category.id);  // ← Sequential deletion
         mergedCategories.push({category: category, index: index});
       }

      const response = await api.post('/categories', {
        parentId: selectedCategory?.id,
        name: newName,
        description: newDescription
      });

      if (!response.data) throw new Error('Failed to create category');
      const newCategory = response.data;

      historyService.recordOperation({
        type: 'mergeCategories',
        data: {
            parentId: selectedCategory.id,
            newCategory: newCategory,
            mergedCategories: mergedCategories
        }
      });

        setCategories(prev => {
             const newCategories = [...prev];
             const parent = findCategoryInTree(newCategories, selectedCategory.id);
             if (parent && parent.children) {
               parent.children = [...(selectedCategory.children || []).filter(child => !selectedCategories.includes(child)), newCategory];
             }
             return newCategories;
           });


       let aggregatedContent: Content[] = []
       for ( const category of selectedCategories) {
            if ( !category.contents ) continue;
            for (const content of category.contents) {
                if ( !aggregatedContent.some(c => c.subcase === content.subcase && c.text === content.text) )
                    aggregatedContent.push(content);
            }
        }

        let newContents: Content[] = [];
        for (const content of aggregatedContent) {
           const response = await api.post(`/content`, {
                parentId: newCategory.id,
                text: content.text,
                subcase: content.subcase
              });
           newContents.push(response.data);
        }

        setCategories(prev => {
          const newCategories = [...prev];
          const parent = findCategoryInTree(newCategories, newCategory.id);
          if (parent) {
            parent.contents = newContents;
          }
          return newCategories;
        });

        setSelectedCategory(newCategory);
        setLoading(false);


   };

  const canRenameCategory = (category: Category | null): boolean => {
    if (!category) return false;

    // Cannot rename root category
    if (!category.parentId) return false;

    return true;
  };

  const canMoveSelectedCategories = (direction: 'up' | 'down'): boolean => {
    if (selectedCategories.length === 0 && selectedContents.length === 0)
        return canMoveCategory(selectedCategory, direction);

    if (selectedContents.length > 0)
        return canMoveContent(direction);

    if (selectedCategories.length === 1)
            return canMoveCategory(selectedCategories[0], direction);

    const parentId = selectedCategories[0].parentId;
    if (!parentId) return false;

    const parent = findParentCategoryInTree(categories, selectedCategories[0].id);

    if (!parent || !parent.children) return false;

    if ( parent.children.length === selectedCategories.length )
        return false;

    // Check if selected categories are consecutive
    const selectedIds = new Set(selectedCategories.map(c => c.id));
    const allIds = parent.children.map(c => c.id);

    // Check if we can move in the requested direction
    const firstSelectedIndex = allIds.indexOf(selectedCategories[0].id);
    const lastSelectedIndex = allIds.indexOf(selectedCategories[selectedCategories.length - 1].id);

    const isNotConsecutive = selectedCategories.length <= Math.abs(lastSelectedIndex - firstSelectedIndex);
    if (isNotConsecutive) return false;

    if (direction === 'up') {
      return Math.min(firstSelectedIndex, lastSelectedIndex) > 0;
    } else {
      return Math.max(firstSelectedIndex, lastSelectedIndex) < allIds.length - 1;
    }
  };

  const canMoveContent = (direction: 'up' | 'down'): boolean => {
    const parentId = selectedContents[0].parentId;
    if (!parentId) return false;


    const parent = findParentCategoryInTree(categories, selectedContents[0].id);
    if (!parent || !parent.contents) return false;

    if ( parent.contents.length === selectedContents.length )
        return false;

    // Check if selected categories are consecutive
    const selectedIds = new Set(selectedContents.map(c => c.id));
    const allIds = parent.contents.map(c => c.id);

    // Check if we can move in the requested direction
    const firstSelectedIndex = allIds.indexOf(selectedContents[0].id);
    const lastSelectedIndex = allIds.indexOf(selectedContents[selectedContents.length - 1].id);

    const isNotConsecutive = selectedContents.length <= Math.abs(lastSelectedIndex - firstSelectedIndex);
    if (isNotConsecutive) return false;

    if (direction === 'up') {
      return Math.min(firstSelectedIndex, lastSelectedIndex) > 0;
    } else {
      return Math.max(firstSelectedIndex, lastSelectedIndex) < allIds.length - 1;
    }
  };

  const canRenameSelectedCategory = (): boolean => {
    return selectedCategories.length === 1 || selectedCategories.length === 0 && !!selectedCategory && !!selectedCategory.parentId;
  };

  const handleDeleteSelectedCategories = async () => {
    if (!canDeleteSelectedCategories()) return;

    try {
      setLoading(true);
      for (const category of selectedCategories) {
        await deleteCategory(category.id);  // ← Sequential deletion
      }

      // Update the categories list
      setCategories(prev => {
        const newCategories = [...prev];
        selectedCategories.forEach(category => {
          const parent = findParentCategoryInTree(newCategories, category.id);
          if (parent && parent.children) {
            parent.children = parent.children.filter(child => child.id !== category.id);
          }
          const index = newCategories.findIndex(c => c.id === category.id);
          if (index !== -1) {
            newCategories.splice(index, 1);
          }
        });
        return newCategories;
      });
        setLoading(false);
      // Clear selection
    setSelectedCategory(null);
      setSelectedCategories([]);
    } catch (error) {
      setLoading(false);
      console.error('Error deleting categories:', error);
      alert('Failed to delete categories');
    }
  };

  const handleCopySelectedCategories = () => {
    if (!canCopySelectedCategories()) return;
    setClipboardCategory(selectedCategories[0]); // For now, we'll only copy the first selected category
    setIsCutOperation(false);
  };

  const handleCutSelectedCategories = () => {
    if (!canCutSelectedCategories()) return;
    setClipboardCategory(selectedCategories[0]); // For now, we'll only cut the first selected category
    setIsCutOperation(true);
  };

  const handleMoveSelectedCategories = async (direction: 'up' | 'down') => {
    if (!canMoveSelectedCategories(direction)) return;

    try {
      const parentId = selectedCategories[0].parentId;
      if (!parentId) return;

      const parent = findParentCategoryInTree(categories, parentId);
      if (!parent || !parent.children) return;

      const allIds = [...parent.children].map(c => c.id);
      const selectedIds = selectedCategories.map(c => c.id);

      // Find the indices of the selected categories
      const indices = selectedIds.map(id => allIds.indexOf(id));

      // Move the categories
      if (direction === 'up') {
        // Move up by swapping with the previous category
        indices.forEach(index => {
          if (index > 0) {
            [allIds[index], allIds[index - 1]] = [allIds[index - 1], allIds[index]];
          }
        });
    } else {
        // Move down by swapping with the next category
        indices.reverse().forEach(index => {
          if (index < allIds.length - 1) {
            [allIds[index], allIds[index + 1]] = [allIds[index + 1], allIds[index]];
          }
        });
      }

      // Update the categories list
      setCategories(prev => [...prev]);
    } catch (error) {
      console.error('Error moving categories:', error);
      alert('Failed to move categories');
    }
  };

  const deleteCategory = async (categoryId: string) => {
    try {
      await api.delete('/categories', { params: { categoryId } });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        localStorage.removeItem('token');
        window.location.replace('/login');
        return;
      }
      console.error('Error deleting category:', error);
      throw error;
    }
  };

  const deleteContent = async (contentId: string) => {
      try {
        await api.delete('/content', { params: { contentId } });
      } catch (error) {
        console.error('Error deleting category:', error);
        throw error;
      }
  };


  const handleDragStart = (category: Category) => {
    setClipboardCategory(category);
    setIsCutOperation(true);
  };

  const handleDragOver = (category: Category) => {
    // Prevent dropping on itself or its children
    if (clipboardCategory &&
        (category.id === clipboardCategory.id ||
         isDescendant(category, clipboardCategory))) {
      return;
    }
    // The visual feedback is handled by the CategoryTree component
  };


  const handleDropCategory = async (sourceCategory: Category, targetCategory: Category, position: 'before' | 'after' | 'inside', copy: boolean) => {
    try {
     var  newCategory: Category = sourceCategory;
      if (position === 'inside') {
        setLoading(true);
        if ( copy ) {
          const response = await api.post(`/categories`, {
            name: sourceCategory.name,
            description: sourceCategory.description,
            parentId: targetCategory.id
          });
          newCategory = response.data;

          historyService.recordOperation({
              type: 'dropCategory',
              data: {
                category: newCategory,
                oldParentId: null,
                newParentId: newCategory.parentId,
                oldIndex: -1,
                newIndex: -1
              }
            });
        }
        else {
            const response = await api.put(`/categories/move`, {
              categoryId: sourceCategory.id,
              newParentId: targetCategory.id
            },
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            });

            newCategory = response.data;

            const parent  = findParentCategoryInTree(categories, sourceCategory.id);
            historyService.recordOperation({
                type: 'dropCategory',
                data: {
                  category: newCategory,
                  oldCategoryId: sourceCategory.id,
                  oldParentId: parent!.id,
                  newParentId: newCategory.parentId,
                  oldIndex: parent!.children!.findIndex(c => c.id === sourceCategory.id) ?? -1,
                  newIndex: -1
                }
              });
        }

        await addSubTree(newCategory, sourceCategory);
        setCategories(prevCategories => {
            let updatedCategories = [...prevCategories];

            // If it's a cut operation, remove the category from its original location
            if (!copy) {
              const removeCategoryFromTree = (cats: Category[], targetId: string): Category[] => {
                        return cats.filter(cat => {
                          if (cat.id === targetId) return false;
                  if (cat.children) {
                        cat.children = removeCategoryFromTree(cat.children, targetId);
                      }
                      return true;
                    });
                  };

              updatedCategories = removeCategoryFromTree(updatedCategories, sourceCategory.id);
              const originalParent = findParentCategoryInTree(updatedCategories, sourceCategory.id);
              if (originalParent) {
                // Update the original parent to remove the category
                const updatedOriginalParent = {
                  ...originalParent,
                  children: (originalParent.children || []).filter(child => child.id !== sourceCategory.id)
                };
                // Update the tree with the modified original parent
                updatedCategories = updateCategoryInTree(updatedCategories, originalParent.id, () => updatedOriginalParent);
              }
            }

            // Update the target parent with the new category
            const updatedTargetParent = {
              ...targetCategory,
              children: [...(targetCategory.children || []), newCategory]
            };

            // Update the tree with the modified target parent
            const newCategories = updateCategoryInTree(updatedCategories, targetCategory.id, () => updatedTargetParent);

            // Select the new category and expand the parent
            setTimeout(() => {
              setSelectedCategory(newCategory);
              setSelectedCategories([]);
              setExpandedNodes(prev => [...prev, targetCategory.id]);
            }, 0);

            return newCategories;
        });
        setLoading(false);
    } else {
        // For before/after positions, we need to determine how many positions to move
        const siblings = getSiblings(categories, targetCategory.id);

       const sourceIndex = siblings.findIndex(cat => cat.id === sourceCategory.id);

       let targetIndex = position === 'after' ?
            siblings.findIndex(cat => cat.id === targetCategory.id) :
            siblings.findIndex(cat => cat.id === targetCategory.id) - 1;

       if ( sourceIndex > targetIndex)
            targetIndex++;
        if ( targetIndex < 0 )
            targetIndex = 0;

        if ( sourceIndex === targetIndex )
            return;

        setLoading(true);
        await api.put(`/categories/moveto`,
            {
                categoryId: sourceCategory.id,
                moveTo: targetIndex
            },
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            }
         );

         historyService.recordOperation({
             type: 'moveCategoryTo',
             data: {
               categoryId: sourceCategory.id,
               fromIndex: sourceIndex,
               toIndex: targetIndex
             }
           });

          setCategories(prev => {
             const newCategories = [...prev];
             const parent = findParentCategoryInTree(newCategories, sourceCategory.id);
             if (parent && parent.children) {
               const child = parent.children[sourceIndex];
               parent.children?.splice(sourceIndex, 1);
               parent.children?.splice(targetIndex, 0, child);
             }
             return newCategories;
           });
           setLoading(false);
      }

    } catch (error) {
      setLoading(false);
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        localStorage.removeItem('token');
        window.location.replace('/login');
        return;
      }
      console.error('Error moving category:', error);
    }
  };

  const handleDropContents = async (contents: Content[], targetCategory: Category, position: 'before' | 'after' | 'inside', copy: boolean) => {
    if (position === 'inside') {
        const duplicates = contents.filter(content =>
            hasDuplicateContent(targetCategory, content.subcase, content.text)
        );

        if (duplicates.length > 0) {
            const duplicateNames = duplicates.map(c => `"${c.subcase}"`).join(', ');
            const message = `The following contents already exist in this category: ${duplicateNames}. Please modify the subcases or choose a different category.`;
            alert(message);
            return;
        }

        let newContents: Content[] = [];
        let response = null;
        const oldParent = findParentCategoryInTree(categories, contents[0].id);

        const operationData: { content: Content;
                oldContentId: string;
                oldParentId: string;
                oldIndex: number;
               }[] = [];

        let sortedContext: {content: Content, index: number}[] = [];
        for ( const content of contents ) {
             const index = oldParent!.contents!.findIndex(c => c.id === content.id) ?? -1;
             sortedContext.push({content, index});
        }
        sortedContext.sort((a, b) => a.index - b.index);
        setLoading(true);
        for ( const {content, index} of sortedContext ) {
              const oldContentId = content.id;

            if (!copy) {
              response = await api.put(`/content/move`, {
                contentId:  content.id,
                newParentId: targetCategory.id
              },
              {
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded'
                }
              });
            } else {
              response = await api.post(`/content`, {
                parentId: targetCategory.id,
                text: content.text,
                subcase: content.subcase
              });
            }
            const newContent = response.data;
            newContents.push(newContent);
            operationData.push({content: newContent, oldContentId: oldContentId, oldParentId: oldParent!.id, oldIndex: index});
        }

        setLoading(false);
        historyService.recordOperation({
                type: 'dropContent',
                data: operationData,
                isBatch: !copy
              });

       setCategories(prevCategories => {
           let updatedCategories = [...prevCategories];

           // If it's a cut operation, remove the category from its original location
           if (!copy) {
             const originalParent = findParentCategoryInTree(updatedCategories, contents[0].id);
             if (originalParent) {
               // Update the original parent to remove the category
               const updatedOriginalParent = {
                 ...originalParent,
                 contents: (originalParent.contents || []).filter(child => !contents.includes(child))
               };
               // Update the tree with the modified original parent
               updatedCategories = updateCategoryInTree(updatedCategories, originalParent.id, () => updatedOriginalParent);
             }
           }

           // Update the target parent with the new category
           const updatedTargetParent = {
             ...targetCategory,
             contents: [...(targetCategory.contents || []), ...newContents]
           };

           // Update the tree with the modified target parent
           const newCategories = updateCategoryInTree(updatedCategories, targetCategory.id, () => updatedTargetParent);

           const updatedTarget = findCategoryInTree(newCategories, targetCategory.id);
          setTimeout(() => {
              if (updatedTarget) setSelectedCategory(updatedTarget);
            }, 0);


           return newCategories;
         });

    }
  };

  const handleDropCategoriesBetween = async (cats: Category[], targetCategory: Category, position: 'before' | 'after' | 'inside') => {
      if ( !targetCategory || !targetCategory.parentId ) return;
      const parent = findCategoryInTree(categories, targetCategory.parentId);
      if (!parent || !parent.children) return;
      const siblings = parent.children.map(c => c.id);

      const selectedIds = cats.map(c => c.id);

            // Find the indices of the selected categories
      const indices = selectedIds.map(id => siblings.indexOf(id)).sort((a, b) => a - b);

      const firstIndex = indices[0];
      const lastIndex = indices[indices.length - 1]
      const isNotConsecutive = selectedIds.length <= Math.abs(lastIndex - firstIndex);
      if (isNotConsecutive) return false;

      let targetIndex = position === 'after' ?
             siblings.findIndex(id => id === targetCategory.id) :
             siblings.findIndex(id => id === targetCategory.id) - 1;

      if ( targetIndex < firstIndex)
          targetIndex++;

      if ( indices.some(c => c === targetIndex ) )
         return;

      setLoading(true);
      await api.put(`/categories/movechildto`, {
         categoryId: parent.id,
         firstIndex,
         lastIndex,
         moveToIndex: targetIndex
       },
       {
         headers: {
           'Content-Type': 'application/x-www-form-urlencoded'
         }
       });

      setLoading(false);

      historyService.recordOperation({
          type: 'dragCategoryTo',
          data: {
            parentId: parent.id,
            firstIndex: firstIndex,
            lastIndex: lastIndex,
            toIndex: targetIndex
          }
        });
       const updatedCategory = await fetchCategoryWithChildren(parent.id);

       setCategories(prevCategories => {
         const updatedCategories = [...prevCategories];
         return updateCategoryInTree(updatedCategories, parent.id, () => updatedCategory);
       });

       setTimeout(() => {
            setSelectedCategory(updatedCategory);
            //setSelectedCategories([]);
            setExpandedNodes(prev => [...prev, updatedCategory.id]);
          }, 0);
     };

   const handleDropContentsBetween = async (contents: Content[], targetContent: Content, position: 'before' | 'after' | 'inside') => {
        const parent = findCategoryInTree(categories, targetContent.parentId);
        if (!parent || !parent.contents) return;
        const siblings = parent.contents.map(c => c.id);

        const selectedIds = contents.map(c => c.id);

              // Find the indices of the selected categories
        const indices = selectedIds.map(id => siblings.indexOf(id)).sort((a, b) => a - b);

        const firstIndex = indices[0];
        const lastIndex = indices[indices.length - 1]
        const isNotConsecutive = selectedIds.length <= Math.abs(lastIndex - firstIndex);
        if (isNotConsecutive) return false;

        let targetIndex = position === 'after' ?
               siblings.findIndex(id => id === targetContent.id) :
               siblings.findIndex(id => id === targetContent.id) - 1;

        if ( targetIndex < firstIndex)
            targetIndex++;

        if ( indices.some(c => c === targetIndex ) )
           return;

        setLoading(true);
        await api.put(`/categories/movechildto`, {
           categoryId: parent.id,
           firstIndex,
           lastIndex,
           moveToIndex: targetIndex
         },
         {
           headers: {
             'Content-Type': 'application/x-www-form-urlencoded'
           }
         });

        setLoading(false);

        historyService.recordOperation({
            type: 'dragContentTo',
            data: {
              parentId: parent.id,
              firstIndex: firstIndex,
              lastIndex: lastIndex,
              toIndex: targetIndex
            }
          });
         const updatedCategory = await fetchCategoryWithChildren(parent.id);

         setCategories(prevCategories => {
           const updatedCategories = [...prevCategories];
           return updateCategoryInTree(updatedCategories, parent.id, () => updatedCategory);
         });

         setTimeout(() => {
              setSelectedCategory(updatedCategory);
              //setSelectedCategories([]);
              setExpandedNodes(prev => [...prev, updatedCategory.id]);
            }, 0);
   };

  // Helper function to check if a category is a descendant of another
  const isDescendant = (parent: Category, child: Category): boolean => {
    if (!parent.children) return false;
    return parent.children.some(cat =>
      cat.id === child.id || isDescendant(cat, child)
    );
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    resizeStartY.current = e.clientY;
    resizeStartHeight.current = contentPanelHeight;
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  };

  const handleResizeMove = (e: MouseEvent) => {
    const delta = resizeStartY.current - e.clientY;
    const newHeight = Math.max(100, Math.min(window.innerHeight - 200, resizeStartHeight.current + delta));
    setContentPanelHeight(newHeight);
  };

  const handleResizeEnd = () => {
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  };

  const handleRightPaneResizerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isRightPaneDraggingRef.current = true;
  };

  const handleContentSave = async (subcase: string, text: string) => {
    if (!selectedCategory) {
      console.error('No category selected');
      return;
    }

    try {
      const prevContent = selectedContents.length > 0 ? selectedContents[0] : null;
      setLoading(true);
      const response = await api.post('/content', {
        parentId: selectedCategory.id,
        text,
        subcase,
        id: prevContent?.id
      });

      if (response.status === 200) {
        const newContent = response.data;
        if (selectedContents.length === 0) {
          historyService.recordOperation({
            type: 'addContent',
            data: {
              content: newContent,
              categoryId: selectedCategory.id
            }
          });
        } else {
          historyService.recordOperation({
            type: 'updateContent',
            data: {
              content: newContent,
              prevContent: prevContent,
              categoryId: selectedCategory.id
            }
          });
        }

        // Refresh the selected category's contents
        const updatedCategory = await fetchCategoryWithChildren(selectedCategory.id);

        setCategories(prevCategories => {
          const updatedCategories = [...prevCategories];
          return updateCategoryInTree(updatedCategories, selectedCategory.id, () => updatedCategory);
        });
        setLoading(false);

        // Update the selected category to show the new content
        setSelectedCategory(updatedCategory);
        setCurrentContent(null);
        setSubcases([]);
        setIsEditingContent(false);
        setShowEditorWarning(false); // Clear the warning banner

        // Update subcases list and set selected subcase

        if (activeDocument) {
          if (!activeDocument.subcases) {
            activeDocument.subcases = [];
          }
          if (!activeDocument.subcases.includes(subcase)) {
            activeDocument.subcases.push(subcase);
          }
          setSelectedSubcase(subcase);
        }
      }
    } catch (error) {
      setLoading(false);
      console.error('Error saving content:', error);
      alert('Failed to save content');
    }
  };

  const handleContentCancel = () => {
    setCurrentContent(selectedContents.length === 1 ? selectedContents[0] : null);
    setIsEditingContent(false);
    setShowEditorWarning(false); // Clear the warning banner
  };

  // Add handlers for unsaved changes confirmation dialog
  const handleUnsavedChangesConfirm = () => {
    if (unsavedChangesDialog.action) {
      unsavedChangesDialog.action();
    }
    setUnsavedChangesDialog({ open: false, action: null, message: '' });
  };

  const handleUnsavedChangesCancel = () => {
    setUnsavedChangesDialog({ open: false, action: null, message: '' });
  };

  const checkForUnsavedChanges = (action: () => void, message: string) => {
    if (isEditingContent) {
      setShowEditorWarning(true);
    } else {
      action();
    }
  };

  const canAddContent = (): boolean => {
    if ( isEditingContent ) return false;
    if (selectedContents.length > 1)
        return false;
    if (!selectedCategory) return false;
    return selectedCategory.contentCategory;
  };

   const canEditContent = (): boolean => {
      if ( isEditingContent ) return false;
     return selectedContents.length === 1;
    };

  const handleAddContent = async () => {
    if (!canAddContent()) return;
    setIsEditingContent(true);
    if (selectedContents.length === 1) {
      const selectedContent = selectedContents[0];
      setCurrentContent(selectedContent);
    } else {
      let initialText = '';
      if (autoPasteEnabled) {
        try {
          const clipboardText = await navigator.clipboard.readText();
          if (clipboardText) {
            initialText = clipboardText;
          }
        } catch (error) {
          console.error('Failed to read clipboard:', error);
        }
      }
      setCurrentContent({ subcase: selectedSubcase || '', text: initialText });
    }
    setSubcases(activeDocument?.subcases || []);
  };

  const handleDeleteContent = async () => {
    if (!selectedCategory || !selectedContents.length) return;

    try {
      setLoading(true);
      // Record the operation before deleting
      historyService.recordOperation({
        type: 'deleteContent',
        data: selectedContents.map(content => ({
          content,
          categoryId: selectedCategory.id,
          index: selectedCategory.contents!.findIndex(c => c.id === content.id) ?? -1
        })),
        isBatch: true
      });

      for (const content of selectedContents) {
        await api.delete('/content', { params: { contentId: content.id } });
      }

      setCategories(prev => {
        const newCategories = [...prev];
        const category = findCategoryInTree(newCategories, selectedCategory.id);
        if (category) {
          category.contents = category.contents?.filter(
            content => !selectedContents.some(selected => selected.id === content.id)
          );
        }
        return newCategories;
      });
      setLoading(false);
      setSelectedContents([]);
    } catch (error) {
      setLoading(false);
      console.error('Error deleting content:', error);
      alert('Failed to delete content');
    }
  };

  const handleAddCategoryDialogClose = () => {
    setAddCategoryDialogOpen(false);
    setNewName('');
    setNewDescription('');
    setAddError('');
  };

  const handleAddCategoryDialogConfirm = async () => {
    if (!newName.trim()) {
      setAddError('Category name is required');
      return;
    }
    await handleAddCategory();
    setAddCategoryDialogOpen(false);
  };

  const handleAddCategoryDialogOpen = () => {
    if (!selectedCategory) return;
    setNewName('');
    setNewDescription('');
    setAddError('');
    setAddCategoryDialogOpen(true);
  };

  const handleDeleteCategory = async () => {
    setOpenDeleteCategoryDialog(true);
  };

  const handleDeleteSelectedCategory = async () => {
     if (!selectedCategory || selectedCategories.length > 1) return;
     const categoryToDelete = selectedCategories.length > 0 ? selectedCategories[0] : selectedCategory;

      try {
        const categoryId = categoryToDelete.id;
        const parent = findParentCategoryInTree(categories, categoryId);

        setLoading(true);
        // Record the operation before deleting
        historyService.recordOperation({
          type: 'deleteCategory',
          data: {
            category: categoryToDelete,
            parentId: parent?.id,
            index: parent?.children!.findIndex(c => c.id === categoryToDelete.id) ?? -1
          }

        });

        await api.delete('/categories', { params: { categoryId } });
        setCategories(prev => {
          const newCategories = [...prev];
          const parent = findParentCategoryInTree(newCategories, categoryId);
          if (parent) {
            parent.children = parent.children?.filter(child => child.id !== categoryId);
          }
          return newCategories;
        });
        setLoading(false);
        setSelectedCategory(parent);
      } catch (error) {
        setLoading(false);
        console.error('Error deleting category:', error);
        alert('Failed to delete category');
      }
    };

  // Add useEffect to refresh documents when needed
  useEffect(() => {
    refreshDocuments();
  }, []); // Only run once when component mounts

  const getUserInitials = () => {
    if (!user) return 'U';
    if (user.firstName && user.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    } else if (user.firstName) {
      return user.firstName.charAt(0).toUpperCase();
    }
    return user.username.charAt(0).toUpperCase();
  };

  useEffect(() => {
    if (!user) return;

    const fetchAvatar = async () => {
      try {
        // Only fetch avatar if user.avatarUrl is a relative path
        if (user.avatarUrl && user.avatarUrl.startsWith('/')) {
          const response = await api.get('/user/avatar', {
            responseType: 'blob',
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });

          const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/png' });
          const dataUrl = URL.createObjectURL(blob);
          setAvatarDataUrl(dataUrl);
        } else if (user.avatarUrl) {
          // If it's a full URL (like Google profile), use it directly
          setAvatarDataUrl(null);
        }
      } catch (error) {
        console.error('Error fetching avatar:', error);
        setAvatarDataUrl(null);
      }
    };

    fetchAvatar();

    return () => {
      if (avatarDataUrl) {
        URL.revokeObjectURL(avatarDataUrl);
      }
    };
  }, [user?.id]);  // Only depend on user ID

  const getAvatarUrl = () => {
    // If user has a full URL (like Google profile), use it directly
    if (user?.avatarUrl && !user.avatarUrl.startsWith('/')) {
      return user.avatarUrl;
    }
    // Otherwise use the blob URL if we have one
    return avatarDataUrl || undefined;
  };

  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('avatar', file);

    try {
      // Close the menu immediately
      setAvatarMenuAnchorEl(null);

      await api.post('/user/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Fetch the new avatar immediately
      const response = await api.get('/user/avatar', {
        responseType: 'blob',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/png' });
      const dataUrl = URL.createObjectURL(blob);
      setAvatarDataUrl(dataUrl);

      // Refresh user details in the background
      fetchUserDetails();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Error uploading avatar:', error.response?.data);
      } else {
        console.error('Error uploading avatar:', error);
      }
    }
  };

  useEffect(() => {
    const fetchPredefinedAvatars = async () => {
      const newDataUrls: {[key: string]: string} = {};
      for (const avatar of PREDEFINED_AVATARS) {
        try {
          const response = await api.get(`/avatars/${avatar.url.split('/').pop()}`, {
            responseType: 'blob'
          });
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          newDataUrls[avatar.id] = URL.createObjectURL(blob);
        } catch (error) {
          console.error(`Error fetching avatar ${avatar.id}:`, error);
        }
      }
      setPredefinedAvatarDataUrls(newDataUrls);
    };

    fetchPredefinedAvatars();
  }, []);

  const handlePredefinedAvatarSelect = async (avatarUrl: string) => {
    try {
      // Close the menu immediately
      setAvatarMenuAnchorEl(null);

      const filename = avatarUrl.split('/').pop();
      await api.post('/user/avatar/predefined', { avatarUrl: filename });

      // Fetch the new avatar immediately
      const response = await api.get('/user/avatar', {
        responseType: 'blob',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/png' });
      const dataUrl = URL.createObjectURL(blob);
      setAvatarDataUrl(dataUrl);

      // Refresh user details in the background
      fetchUserDetails();
    } catch (error) {
      console.error('Error setting predefined avatar:', error);
    }
  };

  const handleAutoPasteChange = (enabled: boolean) => {
    setAutoPasteEnabled(enabled);
  };

  const handleMergeDocument = async (documentId: string, mergeFromDocumentId: string, newName: string, resolveMissing: MergeMissingResolve, resolveTypeMismatch: MergeTypeMismatchResolve) => {
    try {
      setLoading(true);
      const mergedDocument = await mergeDocument(documentId, mergeFromDocumentId, newName, resolveMissing, resolveTypeMismatch);

      // Fetch subcases for the merged document before opening it
      if (mergedDocument.categoryId) {
        const response = await api.get('/documents/subcases', { params: { documentId: mergedDocument.categoryId } });
        mergedDocument.subcases = response.data;
      }

      setLoading(false);

      // Only add a new tab if we're creating a new document (newName is provided)
      if (newName) {
        await addNewTab(mergedDocument.name, mergedDocument);
      }

      // Open the document
      await handleOpenDocument(mergedDocument);
    } catch (error) {
      setLoading(false);
      console.error('Error merging document:', error);
      alert('Failed to merge documents. Found conflicts.');
    }
  };

  const handleRenameSubcase = () => {
    setOpenRenameSubcaseDialog(true);
  };

  const handleRenameSubcaseClose = () => {
    setOpenRenameSubcaseDialog(false);
  };

  const handleRenameSubcaseSubmit = async (oldSubcase: string, newSubcase: string) => {
    try {
      setLoading(true);
      const response = await api.put('/documents/subcases/rename', {
        oldSubcase,
        newSubcase
      }, { params: { documentId: activeDocument?.id } });

      historyService.recordOperation({
          type: 'renameSubcase',
          data: {
            oldName: oldSubcase,
            newName: newSubcase
          }
        });

      setLoading(false);
      if (response.status === 200) {
        // Update the document's subcases list

       if (activeDocument) {
         await handleOpenDocument(activeDocument);
         activeDocument.subcases.forEach((item, i) => { if (item == oldSubcase) activeDocument.subcases[i] = newSubcase; });
         Array.from(new Set(activeDocument.subcases));
       }
        handleRenameSubcaseClose();
      }
    } catch (error) {
      console.error('Error renaming subcase:', error);
      alert('Failed to rename subcase');
    }
  };

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(subTreeSearch);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [subTreeSearch]);

  const renderMainContent = () => {
    const renderRightPane = () => {
      const mainContent = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}>
          <SplitPane
            leftPane={
              <DndProvider backend={HTML5Backend}>
                <CategoryContent
                  ref={categoryContentRef}
                  categories={categories}
                  selectedCategory={selectedCategory}
                  onCategoryMultiSelect={handleCategoryMultiSelect}
                  onContentMultiSelect={handleContentMultiSelect}
                  selectedCategories={selectedCategories}
                  selectedContents={selectedContents}
                  onSelectAllCategories={handleSelectAllCategories}
                  onSelectAllContents={handleSelectAllContents}
                  onEditContent={handleAddContent}
                  onGroupCategories={handleGroupCategoriesDialogOpen}
                  onMergeCategories={handleMergeCategoriesDialogOpen}
                  onDeleteSelected={handleDelete}
                  onMoveSelected={handleMoveCategory}
                  onCutSelected={handleCutCategory}
                  onCopySelected={handleCopyCategory}
                  canEditContent={canEditContent()}
                  canGroupCategories={canGroupCategories()}
                  canMergeCategories={canMergeCategories()}
                  canDeleteSelected={canDeleteSelectedCategories()}
                  canMoveSelected={(direction) => canMoveSelectedCategories(direction)}
                  canCutSelected={canCutSelectedCategories()}
                  canCopySelected={canCopySelectedCategories()}
                  onDropCategories={handleDropCategoriesBetween}
                  onDropContents={handleDropContentsBetween}
                  matchingContentIds={matchingContentIds}
                />
              </DndProvider>
            }
            rightPane={
              <ContentPanel
                ref={contentPanelRef}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: isEditingContent ? 1 : 0.85,
                  pointerEvents: isEditingContent ? 'auto' : 'none',
                  '& .ql-editor': {
                    color: isEditingContent ? 'inherit' : 'rgba(0, 0, 0, 0.87)',
                    backgroundColor: isEditingContent ? 'inherit' : 'rgba(0, 0, 0, 0.02)',
                  },
                  '& .ql-container': {
                    borderColor: isEditingContent ? 'rgba(0, 0, 0, 0.23)' : 'rgba(0, 0, 0, 0.12)',
                  },
                  // Gray out controls when not in editing mode (except subcase dropdown)
                  '& .MuiButton-root, & .MuiIconButton-root': {
                    opacity: isEditingContent ? 1 : 0.5,
                    pointerEvents: isEditingContent ? 'auto' : 'none',
                  },
                  '& .MuiButton-root': {
                    opacity: isEditingContent ? 1 : 0.5,
                    pointerEvents: isEditingContent ? 'auto' : 'none',
                  },
                  '& .MuiIconButton-root': {
                    opacity: isEditingContent ? 1 : 0.5,
                    pointerEvents: isEditingContent ? 'auto' : 'none',
                  },
                }}
              >
                <ContentPanelHeader>
                  <ContentPanelTitle>
                    Content Editor
                    {!isEditingContent && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          ml: 1, 
                          color: 'text.secondary',
                          fontStyle: 'italic'
                        }}
                      >
                        (Read-only)
                      </Typography>
                    )}
                  </ContentPanelTitle>
                </ContentPanelHeader>
                <ContentPanelContent
                  sx={{ flex: 1, overflow: 'auto' }}
                >
                  <ContentEditor
                    subcases={activeDocument?.subcases || []}
                    initialSubcase={currentContent?.subcase}
                    initialText={currentContent?.text}
                    onSave={handleContentSave}
                    onCancel={handleContentCancel}
                    highlightedText={highlightedText}
                    searchTerms={searchTerms}
                    searchMode={searchMode}
                    wholeWord={wholeWord}
                    caseSensitive={caseSensitive}
                    showWarning={showEditorWarning}
                    category={selectedCategory}
                    contentId={selectedContents.length === 1 ? selectedContents[0].id : undefined}
                  />
                </ContentPanelContent>
              </ContentPanel>
            }
            initialSplitPosition={rightPaneSplitPosition}
            minSplitPosition={30}
            maxSplitPosition={90}
            isHorizontal={true}
            hideResizer={!isContentPanelOpen}
          />
        </Box>
      );

      if (isSearchOpen) {
        return (
          <SplitPane
            leftPane={mainContent}
            rightPane={
              <Box sx={{ height: '100%', width: '100%' }}>
                <SearchSettingsProvider>
                  <SemanticSearchWindow
                    open={isSearchOpen}
                    onClose={() => setIsSearchOpen(false)}
                    documentId={activeDocument?.id || ''}
                  />
                </SearchSettingsProvider>
              </Box>
            }
            initialSplitPosition={70}
            minSplitPosition={50}
            maxSplitPosition={90}
          />
        );
      }

      return mainContent;
    };

    return (
      <>
        <StyledTabs
          value={currentTab || false}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.id}
              value={tab.id}
              label={
                <TabLabel>
                  {tab.label}
                  <CloseButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCloseTab(e, tab.id);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </CloseButton>
                </TabLabel>
              }
            />
          ))}
        </StyledTabs>
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {tabs.map((tab) => (
            <Box
              key={tab.id}
              role="tabpanel"
              hidden={currentTab !== tab.id}
              sx={{
                height: '100%',
                display: currentTab === tab.id ? 'flex' : 'none',
                flexDirection: 'column',
                flex: 1,
                backgroundColor: '#ffffff',
              }}
            >
              <SplitPane
                leftPane={
                  <DndProvider backend={HTML5Backend}>
                    <CustomDragLayer />
                    <CategoryTree
                      documentName={tab.label}
                      categories={categories}
                      selectedCategoryId={selectedCategory?.id || null}
                      onCategorySelect={handleCategorySelect}
                      onCategoriesChange={handleCategoriesChange}
                      onSplitCategory={handleSplitDialogOpen}
                      onAddCategory={handleAddCategoryDialogOpen}
                      onAddContent={handleAddContent}
                      onDeleteCategory={handleDelete}
                      onFlattenCategory={handleFlattenCategory}
                      onRenameCategory={handleRenameCategory}
                      onMoveCategory={handleMoveCategory}
                      onCutCategory={handleCutCategory}
                      onCopyCategory={handleCopyCategory}
                      onPasteCategory={handlePasteCategory}
                      canAddCategory={canAddCategory}
                      canDeleteCategory={canDeleteCategory}
                      canFlattenCategory={canFlattenCategory}
                      canMoveCategory={canMoveCategory}
                      canCutCategory={canCutCategory}
                      canCopyCategory={canCopyCategory}
                      canPasteCategory={canPaste}
                      canSplitCategory={!!selectedCategory}
                      canRenameCategory={canRenameCategory}
                      clipboardCategory={clipboardCategory}
                      clipboardContents={clipboardContents}
                      expandedNodes={expandedNodes}
                      onNodeToggle={handleNodeToggle}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDropCategory={handleDropCategory}
                      onDropContents={handleDropContents}
                      category={selectedCategory || categories[0]}
                      onCategoryMultiSelect={handleCategoryMultiSelect}
                      selectedCategories={selectedCategories}
                      onGenerateSubtree={handleGenerateSubtreeDialogOpen}
                      canGenerateSubtree={canGenerateSubtree}
                      onShowSubtree={() => setShowSubTreeDialogOpen(true)}
                    />
                  </DndProvider>
                }
                rightPane={
                  <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
                    {renderRightPane()}
                    {subTreeOverlayOpen && subTreeOverlayData.category && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          width: '100%',
                          height: '100%',
                          backgroundColor: '#fff',
                          zIndex: 1000,
                          padding: 2,
                          overflow: 'auto',
                        }}
                      >
                        <IconButton
                          onClick={() => setSubTreeOverlayOpen(false)}
                          sx={{ position: 'absolute', top: 8, right: 8 }}
                        >
                          <CloseIcon />
                        </IconButton>
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                          <IconButton
                            onClick={() => setSubTreeOverlayOpen(false)}
                            sx={{ position: 'absolute', top: 8, right: 8 }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                        <SubTreeView
                          rootCategory={subTreeOverlayData.category}
                          allCategories={categories}
                          subcases={selectedSubcases}
                          includeDescriptions={true}
                        />
                      </Box>
                    )}
                  </Box>
                }
                initialSplitPosition={30}
                minSplitPosition={20}
                maxSplitPosition={80}
              />
            </Box>
          ))}
        </Box>
      </>
    );
  };

  useEffect(() => {
    if (showSubTreeDialogOpen && activeDocument?.id) {
      setLoadingSubcaseGroups(true);
      api.get('/documents/subcase-groups', { params: { documentId: activeDocument?.categoryId } })
        .then(res => {
          const groupsMap = res.data;
          const groupsArray = Object.entries(groupsMap).map(([name, subcases]) => ({
            name,
            subcases: subcases as string[],
          }));
          setSubcaseGroups(groupsArray);
        })
        .catch(() => setSubcaseGroups([]))
        .finally(() => setLoadingSubcaseGroups(false));
    }
  }, [showSubTreeDialogOpen, activeDocument?.id]);

  useEffect(() => {
    if (subTreeOverlayOpen && selectedCategory) {
      setSubTreeOverlayData(prev => ({
        ...prev,
        category: selectedCategory  // This updates the category
      }));
    }
  }, [selectedCategory, subTreeOverlayOpen]);

  // Load subcase groups when find dialog opens
  useEffect(() => {
    if (findDialogOpen && activeDocument?.id) {
      setLoadingSubcaseGroups(true);
      api.get('/documents/subcase-groups', { params: { documentId: activeDocument?.categoryId } })
        .then(res => {
          const groupsMap = res.data;
          const groupsArray = Object.entries(groupsMap).map(([name, subcases]) => ({
            name,
            subcases: subcases as string[],
          }));
          setSubcaseGroups(groupsArray);
        })
        .catch(() => setSubcaseGroups([]))
        .finally(() => setLoadingSubcaseGroups(false));
    }
  }, [findDialogOpen, activeDocument?.id]);

  // Load subcase groups when duplicate category dialog opens
  useEffect(() => {
    if (duplicateCategoryDialogOpen && activeDocument?.id) {
      setLoadingSubcaseGroups(true);
      api.get('/documents/subcase-groups', { params: { documentId: activeDocument?.categoryId } })
        .then(res => {
          const groupsMap = res.data;
          const groupsArray = Object.entries(groupsMap).map(([name, subcases]) => ({
            name,
            subcases: subcases as string[],
          }));
          setSubcaseGroups(groupsArray);
        })
        .catch(() => setSubcaseGroups([]))
        .finally(() => setLoadingSubcaseGroups(false));
    }
  }, [duplicateCategoryDialogOpen, activeDocument?.id]);

  const handleSubcaseGroupSelect = (groupName: string) => {
    const group = subcaseGroups.find(g => g.name === groupName);
    if (group) {
      // Only one group can be selected at a time
      if (selectedSubcaseGroup === groupName) {
        // Deselect the group - reset to all subcases
        setSelectedSubcases(activeDocument?.subcases || []);
        setSelectedSubcaseGroup(null);
      } else {
        // Select this group - reset selection to only group members
      setSelectedSubcases(group.subcases);
      setSelectedSubcaseGroup(groupName);
      }
    }
  };

  useEffect(() => {
    if (showSubTreeDialogOpen) {
      setSelectedSubcases(activeDocument?.subcases || []);
      setSelectedSubcaseGroup(null);
    }
  }, [showSubTreeDialogOpen, activeDocument]);

  useEffect(() => {
    setSubTreeOverlayOpen(false);
  }, [currentTab]);

  const handleSubcaseGroupsClick = () => {
    setSubcaseGroupsDialogOpen(true);
  };

  const handleSubcaseChange = (event: any) => {
    const newSubcase = event.target.value;
    setSelectedSubcase(newSubcase);
  };

  const handleNodeToggle = (nodeIds: string[]) => {
    setExpandedNodes(nodeIds);
    // Save the expanded state for the current document
    if (currentDocument) {
      setDocumentExpandedStates(prev => ({
        ...prev,
        [currentDocument.id]: nodeIds
      }));
    }
  };

  const handleFontSizeChange = (newSize: number) => {
    setFontSize(newSize);
    localStorage.setItem('fontSize', newSize.toString());
    // Update global CSS variable
    document.documentElement.style.setProperty('--app-font-size', `${newSize}px`);
  };

  // Category find functionality
  const performBreadthFirstSearch = (startCategory: Category, searchString: string): Category[] => {
    const results: Category[] = [];
    const queue: Category[] = [startCategory];

    while (queue.length > 0) {
      const currentCategory = queue.shift()!;

      // Check if current category name contains the search string
      if (currentCategory.name.toLowerCase().includes(searchString.toLowerCase())) {
        results.push(currentCategory);
      }

      // Add children to queue for breadth-first search
      if (currentCategory.children) {
        queue.push(...currentCategory.children);
      }
    }

    return results;
  };

  const handleFindCategory = () => {
    setFindDialogOpen(true);
  };

  const canGenerateSubtree = (category: Category | null): boolean => {
    if (!category || !category.contentCategory || !category.contents) return false;
    return category.contents.length >1;
  };

  const handleGenerateSubtreeDialogOpen = () => {
    if (canGenerateSubtree(selectedCategory)) {
      setGenerateSubtreeDialogOpen(true);
    }
  };

  const handleGenerateSubtreeDialogClose = () => {
    setGenerateSubtreeDialogOpen(false);
  };

  const handleGenerateSubtreeSuccess = async (updatedCategoryId?: string) => {
    const categoryIdToFetch = updatedCategoryId || selectedCategory?.id;
    if (categoryIdToFetch) {
      try {
        setLoading(true);
        const updatedCategory = await fetchCategoryWithChildren(categoryIdToFetch);
        setLoading(false);
        if (updatedCategory) {
          setSelectedCategory(updatedCategory);
          setCategories(prev => {
            // Helper to recursively replace the category in the tree
            const replaceCategoryInTree = (cats: Category[]): Category[] => {
              return cats.map(cat => {
                if (cat.id === (selectedCategory?.id || updatedCategory.id)) {
                  // Replace the old category with the updated one
                  return updatedCategory;
                }
                if (cat.children) {
                  return {
                    ...cat,
                    children: replaceCategoryInTree(cat.children)
                  };
                }
                return cat;
              });
            };

            return replaceCategoryInTree(prev);
          });
          setExpandedNodes(prev => [...prev, updatedCategory.id]);
        }
      } catch (error) {
        setLoading(false);
        console.error('Error refreshing category after generating subtree:', error);
      }
    }
  };

  const handleFindDialogClose = () => {
    setFindDialogOpen(false);
    setFindSearchString('');
    setFindResults([]);
    setCurrentFindIndex(0);
    setFindStarted(false);
    setShowEndWarning(false);
    setShowStartWarning(false);
    setHasWrappedAround(false);
    setHighlightedText('');
    setMatchingContentIds([]);
    // Reset position to initial top-right position (calculate dynamically)
    setDialogPosition({ x: Math.max(0, window.innerWidth - 350), y: 80 });
  };

  const handleFindSearch = () => {
    if (!findSearchString.trim()) return;

    // Determine starting category: selected category or root
    const startCategory = selectedCategory || categories[0];
    if (!startCategory) return;

    // Perform breadth-first search
    const results = performBreadthFirstSearch(startCategory, findSearchString);
    setFindResults(results);
    setCurrentFindIndex(0);
    setFindStarted(true);
    setShowEndWarning(false);
    setShowStartWarning(false);

    if (results.length > 0) {
      // Select the first result and expand its parents to make it visible
      const targetCategory = results[0];
      setSelectedCategory(targetCategory);
      expandParentsToMakeVisible(targetCategory);
      } else {
      // Show warning in dialog instead of alert
      setShowEndWarning(true);
      setTimeout(() => setShowEndWarning(false), 3000);
    }
  };

  const handleFindNext = () => {
    if (findResults.length === 0) return;

    const nextIndex = currentFindIndex + 1;

    if (nextIndex >= findResults.length) {
      // Reached the end, automatically continue from the top
      setShowEndWarning(true);
      setHasWrappedAround(true);
      setTimeout(() => setShowEndWarning(false), 3000); // Hide after 3 seconds
      setCurrentFindIndex(0);
      const targetCategory = findResults[0];
      setSelectedCategory(targetCategory);
      expandParentsToMakeVisible(targetCategory);
    } else {
      setShowEndWarning(false);
      setShowStartWarning(false);
      setHasWrappedAround(false);
      setCurrentFindIndex(nextIndex);
      const targetCategory = findResults[nextIndex];
      setSelectedCategory(targetCategory);
      expandParentsToMakeVisible(targetCategory);
    }
  };

  const handleFindPrevious = () => {
    if (findResults.length === 0) return;

    const prevIndex = currentFindIndex - 1;

    if (prevIndex < 0) {
      // Reached the beginning, automatically continue from the end
      setShowStartWarning(true);
      setHasWrappedAround(true);
      setTimeout(() => setShowStartWarning(false), 3000); // Hide after 3 seconds
      setCurrentFindIndex(findResults.length - 1);
      const targetCategory = findResults[findResults.length - 1];
      setSelectedCategory(targetCategory);
      expandParentsToMakeVisible(targetCategory);
    } else {
      setShowEndWarning(false);
      setShowStartWarning(false);
      setHasWrappedAround(false);
      setCurrentFindIndex(prevIndex);
      const targetCategory = findResults[prevIndex];
      setSelectedCategory(targetCategory);
      expandParentsToMakeVisible(targetCategory);
    }
  };

  const expandParentsToMakeVisible = (targetCategory: Category) => {
    const parentIds = getParentIds(targetCategory);
    setExpandedNodes(prev => {
      const newExpanded = [...prev];
      parentIds.forEach(parentId => {
        if (!newExpanded.includes(parentId)) {
          newExpanded.push(parentId);
        }
      });
      return newExpanded;
    });

    // Scroll immediately and also after a delay
    scrollToCategory(targetCategory.id);

    // Also try again after a delay to ensure expansion is complete
    setTimeout(() => {
      scrollToCategory(targetCategory.id);
    }, 1000);

    // And try one more time after a longer delay
    setTimeout(() => {
      scrollToCategory(targetCategory.id);
    }, 2000);
  };

  const scrollToCategory = (categoryId: string) => {

    // Find the category object to get its name
    const targetCategory = findCategoryInTree(categories, categoryId);
    if (!targetCategory) {
      return;
    }

    // Find the tree view container
    const treeView = document.querySelector('.MuiTreeView-root');
    if (!treeView) {
      return;
    }

    // Use the same approach as the working test script
    const treeItems = document.querySelectorAll('.MuiTreeItem-root');

    // Find the tree item that contains the category name
    let targetTreeItem = null;
    for (const treeItem of Array.from(treeItems)) {
      const textContent = treeItem.textContent;
      if (textContent && textContent.includes(targetCategory.name)) {
        targetTreeItem = treeItem;
        break;
      }
    }

    if (targetTreeItem) {
      targetTreeItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      // Fallback: scroll to the last tree item to show that scrolling works
      if (treeItems.length > 0) {
        const lastTreeItem = treeItems[treeItems.length - 1];
        lastTreeItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const getParentIds = (category: Category): string[] => {
    const parentIds: string[] = [];
    const findParents = (cats: Category[], targetId: string, currentPath: string[] = []): boolean => {
      for (const cat of cats) {
        if (cat.id === targetId) {
          parentIds.push(...currentPath);
          return true;
        }
        if (cat.children) {
          if (findParents(cat.children, targetId, [...currentPath, cat.id])) {
            return true;
          }
        }
      }
      return false;
    };

    findParents(categories, category.id);
    return parentIds;
  };

  // Dialog drag handlers
  const handleDialogMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    // Check if the click is on the header area (the gray background area)
    const isHeader = target.closest('[style*="background-color: rgb(240, 240, 240)"]') ||
                    target.closest('[style*="background-color: #f0f0f0"]') ||
                    target.style.backgroundColor === 'rgb(240, 240, 240)' ||
                    target.style.backgroundColor === '#f0f0f0';

    if (isHeader) {
      e.preventDefault();
      setIsDragging(true);
      const rect = e.currentTarget.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleDialogMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Keep dialog within viewport bounds
      const maxX = window.innerWidth - 400; // dialog width
      const maxY = window.innerHeight - 300; // dialog height

      setDialogPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    }
  };

  const handleDialogMouseUp = () => {
    setIsDragging(false);
  };

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDialogMouseMove);
      document.addEventListener('mouseup', handleDialogMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleDialogMouseMove);
        document.removeEventListener('mouseup', handleDialogMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  // Global keyboard shortcuts for find dialog
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Prevent shortcuts when typing in input fields
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        return;
      }

      // Ctrl+F or Cmd+F: Open find dialog
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        if (!findDialogOpen) {
          handleFindCategory();
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [findDialogOpen]);

  const scrollToContent = (contentId: string) => {

    // Find the content object to get its subcase and text
    const findContentById = (cats: Category[], id: string): Content | null => {
      for (const cat of cats) {
        if (cat.contentCategory && cat.contents) {
          const content = cat.contents.find(c => c.id === id);
          if (content) return content;
        }
        if (cat.children) {
          const found = findContentById(cat.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    const targetContent = findContentById(categories, contentId);
    if (!targetContent) {
      return;
    }

    // Find all table rows in the CategoryContent component
    const tableRows = document.querySelectorAll('.MuiTableRow-root');

    // Find the table row that contains the content subcase
    let targetRow = null;
    for (const row of Array.from(tableRows)) {
      const textContent = row.textContent;
      if (textContent && textContent.includes(targetContent.subcase)) {
        // Additional check to make sure this is the right content row
        // Look for the content text as well
        if (textContent.includes(targetContent.text.substring(0, 50))) {
          targetRow = row;
          break;
        }
      }
    }

    if (targetRow) {
      targetRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
    {loading && (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
    justifyContent: 'center',
      }}
    >
      <CircularProgress />
    </Box>
  )}
      <CssBaseline />
      <StyledAppBar position="fixed">
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={() => setIsDocumentPanelVisible(!isDocumentPanelVisible)}
            edge="start"
            sx={{ mr: 2 }}
            disabled={location.pathname.startsWith('/admin')}
          >
            <MenuIcon />
          </IconButton>
          <LogoImage src="/images/logo.png" alt="Narralyze Logo" />
        {!location.pathname.startsWith('/admin') && (
          <Box
            ref={setToolbarRef}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              flexGrow: 1,
              ml: 15,
              flexWrap: 'nowrap',
              minHeight: 48,
              overflow: 'hidden',
            }}
          >
            {/* Dynamic Toolbar Buttons */}
            {visibleButtons.has('add') && (
              <Tooltip title={getToolbarTooltipTextForState('add-category', !!canAddCategory(selectedCategory))}>
                <span>
                  <StyledIconButton
                    onClick={handleAddCategoryDialogOpen}
                    disabled={!canAddCategory(selectedCategory)}
                  >
                    <AddIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {visibleButtons.has('edit') && (
              <Tooltip title={(selectedContents.length !== 1) ?
                    getToolbarTooltipTextForState('add-content', !!canAddContent()) :
                    getToolbarTooltipTextForState('edit-content', !!canEditContent())}>
                <span>
                  <StyledIconButton
                    onClick={handleAddContent}
                    disabled={!canAddContent()}
                  >
                    <EditIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {visibleButtons.has('delete') && (
              <Tooltip title={(selectedContents.length !== 1) ?
                    getToolbarTooltipTextForState('delete-category', !!canDeleteSelectedCategories()):
                    getToolbarTooltipTextForState('delete-content', !!canDeleteSelectedCategories())}>
                <span>
                  <StyledIconButton
                    onClick={handleDelete}
                    disabled={!canDeleteSelectedCategories()}
                  >
                    <DeleteIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {visibleButtons.has('split') && (
              <Tooltip title={getToolbarTooltipTextForState('split-category', !!(selectedCategory && selectedCategory.contentCategory))}>
                <span>
                  <StyledIconButton
                    onClick={handleSplitDialogOpen}
                    disabled={!selectedCategory || !selectedCategory.contentCategory}
                  >
                    <CallSplitIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {visibleButtons.has('group') && (
              <Tooltip title={getToolbarTooltipTextForState('group-categories', !!canGroupCategories())}>
                <span>
                  <StyledIconButton
                    onClick={handleGroupCategoriesDialogOpen}
                    disabled={!canGroupCategories()}
                  >
                    <CallMergeIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {visibleButtons.has('flatten') && (
              <Tooltip title={getToolbarTooltipTextForState('flatten-category', !!canFlattenCategory())}>
                <span>
                  <StyledIconButton
                    onClick={handleFlattenCategory}
                    disabled={!canFlattenCategory()}
                  >
                    <KeyboardDoubleArrowUpIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {visibleButtons.has('merge') && (
              <Tooltip title={getToolbarTooltipTextForState('merge-categories', !!canMergeCategories())}>
                <span>
                  <StyledIconButton
                    onClick={handleMergeCategoriesDialogOpen}
                    disabled={!canMergeCategories()}
                  >
                    <JoinFullIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {visibleButtons.has('generate') && (
              <Tooltip title={getToolbarTooltipTextForState('generate-subtree', canGenerateSubtree(selectedCategory))}>
                <span>
                  <StyledIconButton
                    onClick={handleGenerateSubtreeDialogOpen}
                    disabled={!canGenerateSubtree(selectedCategory)}
                  >
                    <AutoAwesomeIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {visibleButtons.has('rename') && (
              <Tooltip title={getToolbarTooltipTextForState('rename-category', !!canRenameSelectedCategory())}>
                <span>
                  <StyledIconButton
                    onClick={handleRenameCategory}
                    disabled={!canRenameSelectedCategory()}
                  >
                    <DriveFileRenameOutlineIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {visibleButtons.has('moveUp') && (
              <Tooltip title={getToolbarTooltipTextForState(selectedContents.length > 0 ? 'move-content-up' : 'move-category-up', !!canMoveSelectedCategories('up'))}>
                <span>
                  <StyledIconButton
                    onClick={() => handleMoveCategory('up')}
                    disabled={!canMoveSelectedCategories('up')}
                  >
                    <ArrowUpwardIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {visibleButtons.has('moveDown') && (
              <Tooltip title={getToolbarTooltipTextForState(selectedContents.length > 0 ? 'move-content-down' :'move-category-down', !!canMoveSelectedCategories('down'))}>
                <span>
                  <StyledIconButton
                    onClick={() => handleMoveCategory('down')}
                    disabled={!canMoveSelectedCategories('down')}
                  >
                    <ArrowDownwardIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {visibleButtons.has('cut') && (
              <Tooltip title={getToolbarTooltipTextForState('cut-category', !!canCutSelectedCategories())}>
                <span>
                  <StyledIconButton
                    onClick={handleCutCategory}
                    disabled={!canCutSelectedCategories()}
                  >
                    <ContentCutIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {visibleButtons.has('copy') && (
              <Tooltip title={getToolbarTooltipTextForState('copy-category', !!canCopySelectedCategories())}>
                <span>
                  <StyledIconButton
                    onClick={handleCopyCategory}
                    disabled={!canCopySelectedCategories()}
                  >
                    <ContentCopyIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {visibleButtons.has('paste') && (
              <Tooltip title={getToolbarTooltipTextForState('paste-category', !!canPaste(selectedCategory))}>
                <span>
                  <StyledIconButton
                    onClick={handlePasteCategory}
                    disabled={!canPaste(selectedCategory)}
                  >
                    <ContentPasteIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {visibleButtons.has('undo') && (
              <Tooltip title={canUndo ? `Undo ${historyService.getUndoOperationName()}` : getToolbarTooltipTextForState('undo-disabled', false)}>
                <span>
                  <StyledIconButton
                    onClick={handleUndo}
                    disabled={!canUndo}
                  >
                    <UndoIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {visibleButtons.has('redo') && (
              <Tooltip title={canRedo ? `Redo ${historyService.getRedoOperationName()}` : getToolbarTooltipTextForState('redo-disabled', false)}>
                <span>
                  <StyledIconButton
                    onClick={handleRedo}
                    disabled={!canRedo}
                  >
                    <RedoIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {visibleButtons.has('duplicate') && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 4 }}>
                  <Tooltip title={getToolbarTooltipTextForState('duplicate-as-document', !!selectedCategory)}>
                    <span>
                      <StyledIconButton
                        onClick={() => setDuplicateCategoryDialogOpen(true)}
                        disabled={!selectedCategory}
                      >
                        <FileCopyIcon />
                      </StyledIconButton>
                    </span>
                  </Tooltip>
              </Box>
            )}

            {visibleButtons.has('renameSubcase') && (
              <Tooltip title={getToolbarTooltipTextForState('rename-subcase', tabs.length > 0)}>
                <span>
                  <StyledIconButton
                    onClick={handleRenameSubcase}
                    disabled={tabs.length === 0}
                  >
                    <EditNoteIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {visibleButtons.has('subcaseGroups') && (
              <Tooltip title={getToolbarTooltipTextForState('manage-subcase-groups', !!activeDocument)}>
                <span>
                  <StyledIconButton
                    onClick={handleSubcaseGroupsClick}
                    disabled={!activeDocument}
                  >
                    <GroupIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {/* More Actions Button - Only show when buttons are hidden */}
            {(visibleButtons.size < 14) && (
              <Tooltip title="More Actions">
                <span>
                  <StyledIconButton
                    onClick={handleToolbarMenuOpen}
                    disabled={false}
                    sx={{ ml: 0.5 }}
                  >
                    <MoreVertIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {/* Subcase Selector - Always Visible if Available */}
            {activeDocument?.subcases && (
                <Tooltip title={getToolbarTooltipTextForState('select-subcase', true)} placement="right">
                  <span>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <StyledSelect
                        value={selectedSubcase}
                        onChange={handleSubcaseChange}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>Select Subcase</em>
                        </MenuItem>
                        {activeDocument?.subcases?.map((subcase: string) => (
                          <MenuItem key={subcase} value={subcase}>
                            {subcase}
                          </MenuItem>
                        ))}
                      </StyledSelect>
                    </FormControl>
                  </span>
                </Tooltip>
            )}

            {/* Search and AI Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 4 }}>
              <Tooltip title={getToolbarTooltipTextForState('find-in-document', tabs.length > 0)}>
                <span>
                  <StyledIconButton
                    onClick={handleFindCategory}
                    disabled={tabs.length === 0}
                  >
                    <FindInPageIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
              <Tooltip title={getToolbarTooltipTextForState('show-sub-tree', tabs.length > 0 && !!selectedCategory)}>
                <span>
                  <StyledIconButton
                    onClick={() => setShowSubTreeDialogOpen(true)}
                    disabled={tabs.length === 0 || !selectedCategory}
                  >
                    <VisibilityIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
              <Tooltip title={getToolbarTooltipTextForState('ask-ai-prompt', tabs.length > 0)}>
                <span>
                  <StyledIconButton
                    onClick={() => setIsSearchOpen(true)}
                    disabled={tabs.length === 0}
                  >
                    <AiSparklesIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            </Box>



                        {/* Toolbar Menu - Only show buttons that aren't visible on toolbar */}
            <Menu
              anchorEl={toolbarMenuAnchorEl}
              open={Boolean(toolbarMenuAnchorEl)}
              onClose={handleToolbarMenuClose}
              PaperProps={{
                sx: {
                  maxHeight: 400,
                  width: 280,
                  p: 1
                }
              }}
            >
              {/* Category Operations */}
              {!visibleButtons.has('split') && (
                <MenuItem onClick={() => { handleSplitDialogOpen(); handleToolbarMenuClose(); }} disabled={!selectedCategory || !selectedCategory.contentCategory}>
                  <ListItemIcon>
                    <CallSplitIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Split Category" />
                </MenuItem>
              )}
              {!visibleButtons.has('group') && (
                <MenuItem onClick={() => { handleGroupCategoriesDialogOpen(); handleToolbarMenuClose(); }} disabled={!canGroupCategories()}>
                  <ListItemIcon>
                    <CallMergeIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Group Categories" />
                </MenuItem>
              )}
              {!visibleButtons.has('flatten') && (
                <MenuItem onClick={async () => { await handleFlattenCategory(); handleToolbarMenuClose(); }} disabled={!canFlattenCategory()}>
                  <ListItemIcon>
                    <KeyboardDoubleArrowUpIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Flatten Category" />
                </MenuItem>
              )}
              {!visibleButtons.has('merge') && (
                <MenuItem onClick={() => { handleMergeCategoriesDialogOpen(); handleToolbarMenuClose(); }} disabled={!canMergeCategories()}>
                  <ListItemIcon>
                    <JoinFullIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Merge Categories" />
                </MenuItem>
              )}
              {!visibleButtons.has('generate') && (
                <MenuItem onClick={() => { handleGenerateSubtreeDialogOpen(); handleToolbarMenuClose(); }} disabled={!canGenerateSubtree(selectedCategory)}>
                  <ListItemIcon>
                    <AutoAwesomeIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Generate Subtree" />
                </MenuItem>
              )}
              {!visibleButtons.has('rename') && (
                <MenuItem onClick={() => { handleRenameCategory(); handleToolbarMenuClose(); }} disabled={!canRenameSelectedCategory()}>
                  <ListItemIcon>
                    <DriveFileRenameOutlineIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Rename Category" />
                </MenuItem>
              )}

              {/* Movement Operations */}
              {(!visibleButtons.has('moveUp') || !visibleButtons.has('moveDown')) && <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.3)' }} />}
              {!visibleButtons.has('moveUp') && (
                <MenuItem onClick={async () => { await handleMoveCategory('up'); handleToolbarMenuClose(); }} disabled={!canMoveSelectedCategories('up')}>
                  <ListItemIcon>
                    <ArrowUpwardIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Move Up" />
                </MenuItem>
              )}
              {!visibleButtons.has('moveDown') && (
                <MenuItem onClick={async () => { await handleMoveCategory('down'); handleToolbarMenuClose(); }} disabled={!canMoveSelectedCategories('down')}>
                  <ListItemIcon>
                    <ArrowDownwardIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Move Down" />
                </MenuItem>
              )}

              {/* Clipboard Operations */}
              {(!visibleButtons.has('cut') || !visibleButtons.has('copy') || !visibleButtons.has('paste')) && <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.3)' }} />}
              {!visibleButtons.has('cut') && (
                <MenuItem onClick={() => { handleCutCategory(); handleToolbarMenuClose(); }} disabled={!canCutSelectedCategories()}>
                  <ListItemIcon>
                    <ContentCutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Cut" />
                </MenuItem>
              )}
              {!visibleButtons.has('copy') && (
                <MenuItem onClick={() => { handleCopyCategory(); handleToolbarMenuClose(); }} disabled={!canCopySelectedCategories()}>
                  <ListItemIcon>
                    <ContentCopyIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Copy" />
                </MenuItem>
              )}
              {!visibleButtons.has('paste') && (
                <MenuItem onClick={async () => { await handlePasteCategory(); handleToolbarMenuClose(); }} disabled={!canPaste(selectedCategory)}>
                  <ListItemIcon>
                    <ContentPasteIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Paste" />
                </MenuItem>
              )}

              {/* History Operations */}
              {(!visibleButtons.has('undo') || !visibleButtons.has('redo')) && <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.3)' }} />}
              {!visibleButtons.has('undo') && (
                <MenuItem onClick={async () => { await handleUndo(); handleToolbarMenuClose(); }} disabled={!canUndo}>
                  <ListItemIcon>
                    <UndoIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={`Undo ${historyService.getUndoOperationName()}`} />
                </MenuItem>
              )}
              {!visibleButtons.has('redo') && (
                <MenuItem onClick={async () => { await handleRedo(); handleToolbarMenuClose(); }} disabled={!canRedo}>
                  <ListItemIcon>
                    <RedoIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={`Redo ${historyService.getRedoOperationName()}`} />
                </MenuItem>
              )}

              {/* Spacer between History and Document operations */}
              <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.5)', borderWidth: 2 }} />
              <Box sx={{ height: 20 }} />
              {!visibleButtons.has('duplicate') && (
                <MenuItem onClick={() => { setDuplicateCategoryDialogOpen(true); handleToolbarMenuClose(); }} disabled={!selectedCategory}>
                  <ListItemIcon>
                    <FileCopyIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Duplicate as Document" />
                </MenuItem>
              )}
              {!visibleButtons.has('renameSubcase') && (
                <MenuItem onClick={() => { handleRenameSubcase(); handleToolbarMenuClose(); }} disabled={tabs.length === 0}>
                  <ListItemIcon>
                    <EditNoteIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Rename Subcase" />
                </MenuItem>
              )}
              {!visibleButtons.has('subcaseGroups') && (
                <MenuItem onClick={() => { handleSubcaseGroupsClick(); handleToolbarMenuClose(); }} disabled={!activeDocument}>
                  <ListItemIcon>
                    <GroupIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Manage Subcase Groups" />
                </MenuItem>
              )}
            </Menu>
          </Box>
        )}
       <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Help & Support">
            <span>
              <StyledIconButton
                onClick={() => setHelpDialogOpen(true)}
              >
                <HelpIcon />
              </StyledIconButton>
            </span>
          </Tooltip>
          {!location.pathname.startsWith('/admin') && (<SettingsMenu onAutoPasteChange={handleAutoPasteChange} fontSize={fontSize} onFontSizeChange={handleFontSizeChange} />)}
          <IconButton
            color="inherit"
            onClick={handleClick}
            size="small"
            sx={{ ml: 2 }}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >

            {user ? (
              <Avatar
                src={getAvatarUrl()}
                sx={{ width: 40, height: 40, cursor: 'pointer' }}
              />
            ) : (
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: 'primary.main',
                  cursor: 'pointer'
                }}
              >
                {getUserInitials()}
              </Avatar>
            )}
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user?.username || 'User'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email || ''}
              </Typography>
            </Box>
            <MenuItem onClick={() => {
              handleClose();
              setProfileDialogOpen(true);
            }}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={(e) => {
              e.stopPropagation();
              handleAvatarMenuClick(e);
            }}>
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              Change Avatar
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
            <Menu
              anchorEl={avatarMenuAnchorEl}
              open={avatarMenuOpen}
              onClose={handleAvatarMenuClose}
              onClick={(e) => e.stopPropagation()}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              PaperProps={{
                sx: {
                  width: 280,
                  p: 1
                }
              }}
            >
              <MenuItem component="label">
                <ListItemIcon>
                  <UploadIcon fontSize="small" />
                </ListItemIcon>
                Upload New Avatar
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleAvatarChange}
                />
              </MenuItem>
              <Divider />
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 1,
                p: 1,
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                {PREDEFINED_AVATARS.map((avatar) => (
                  <Box
                    key={avatar.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePredefinedAvatarSelect(avatar.url);
                    }}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        transition: 'transform 0.2s'
                      }
                    }}
                  >
                    <Avatar
                      src={predefinedAvatarDataUrls[avatar.id]}
                      sx={{
                        width: 60,
                        height: 60
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Menu>
          </Menu>
          {user?.admin && (
            <Button
              color="secondary"
              variant="contained"
              sx={{ ml: 2 }}
              onClick={() => navigate(location.pathname.startsWith('/admin') ? '/' : '/admin')}
            >
              {location.pathname.startsWith('/admin') ? 'Main UI' : 'Admin Console'}
            </Button>
          )}
        </Toolbar>
      </StyledAppBar>

      <MainContent>
        <SplitPaneContainer>
          {location.pathname.startsWith('/admin') ? (
            <Outlet />
          ) : (
            <>
              {isDocumentPanelVisible && (
                <Pane style={{ width: '250px', position: 'fixed', left: 0, top: '64px', bottom: 0, zIndex: 1 }}>
                  <DocumentPanel
                    onNewDocument={handleNewDocument}
                    documents={documents.map(doc => ({
                      ...doc,
                      hasOpenTab: tabs.some(tab => tab.id === doc.id)
                    }))}
                    onOpenDocument={handleOpenDocument}
                    onCloseDocument={handleCloseDocument}
                    onDeleteDocument={handleDeleteDocument}
                    onRenameDocument={handleRenameDocument}
                    onDuplicateDocument={handleDuplicateDocument}
                    onMergeDocument={handleMergeDocument}
                    onSelectDocument={handleSelectDocument}
                    onLoadFromFile={handleLoadFromFile}
                    onExportDocument={handleExportDocument}
                    currentTab={currentTab}
                    currentDocument={currentDocument}
                    setGlobalLoading={setLoading}
                    refreshCurrentDocument={async () => {
                      if (currentDocument?.categoryId) {
                        const rootCategory = await fetchCategoryWithChildren(currentDocument.categoryId);
                        setCategories([rootCategory]);
                      }
                    }}
                    refreshDocuments={refreshDocuments}
                    tabs={tabs}
                  />
                </Pane>
              )}
              <Pane style={{
                width: '100%',
                marginLeft: isDocumentPanelVisible ? '260px' : '10px',
                paddingLeft: '10px',
                height: '100%',
                overflow: 'hidden'
              }}>
                {renderMainContent()}
              </Pane>
            </>
          )}
        </SplitPaneContainer>
      </MainContent>
      <Dialog open={splitDialogOpen} onClose={handleSplitDialogClose}>
        <DialogTitle>Split Category</DialogTitle>
        <DialogContent>
          {splitError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {splitError}
          </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="First Category Name"
            fullWidth
            value={splitNames.first}
            onChange={(e) => {
              const value = e.target.value;
              setSplitNames(prev => {
                const updated = { ...prev, first: value };
                validateSplitNames(updated.first, updated.second);
                return updated;
              });
            }}
            error={!!splitError}
          />
          <TextField
            margin="dense"
            label="Second Category Name"
            fullWidth
            value={splitNames.second}
            onChange={(e) => {
              const value = e.target.value;
              setSplitNames(prev => {
                const updated = { ...prev, second: value };
                validateSplitNames(updated.first, updated.second);
                return updated;
              });
            }}
            error={!!splitError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSplitDialogClose}>Cancel</Button>
          <Button onClick={handleSplitDialogConfirm} variant="contained" color="primary" disabled={!!splitError}>
            Split
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={addCategoryDialogOpen} onClose={handleAddCategoryDialogClose}>
        <DialogTitle>Add New Category</DialogTitle>
        <DialogContent>
          {addError && (
             <Typography color="error" sx={{ mb: 2 }}>
                {addError}
          </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            value={newName}
            onChange={(e) => handleNewCategoryNameChange(e.target.value)}
            error={!!addError}
          />
          <TextField
              margin="dense"
              label="Description"
              fullWidth
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddCategoryDialogClose}>Cancel</Button>
          <Button onClick={handleAddCategoryDialogConfirm} variant="contained" color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={groupCategoriesDialogOpen} onClose={handleGroupCategoriesDialogClose}>
          <DialogTitle>Group Categories</DialogTitle>
          <DialogContent>
            {addError && (
               <Typography color="error" sx={{ mb: 2 }}>
                  {addError}
            </Typography>
            )}
            <TextField
              autoFocus
              margin="dense"
              label="Parent Category Name"
        fullWidth
              value={newName}
              onChange={(e) => handleNewCategoryNameChange(e.target.value)}
              error={!!addError}
            />
            <TextField
                margin="dense"
                label="Description"
                fullWidth
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleGroupCategoriesDialogClose}>Cancel</Button>
            <Button onClick={handleGroupCategoriesDialogConfirm} variant="contained" color="primary">
              Group
            </Button>
          </DialogActions>
        </Dialog>
      <Dialog open={mergeCategoriesDialogOpen} onClose={handleMergeCategoriesDialogClose}>
        <DialogTitle>Merge Categories</DialogTitle>
        <DialogContent>
          {addError && (
             <Typography color="error" sx={{ mb: 2 }}>
                {addError}
          </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Merged Category Name"
      fullWidth
            value={newName}
            onChange={(e) => handleMergeCategoriesNameChange(e.target.value)}
            error={!!addError}
          />
          <TextField
              margin="dense"
              label="Description"
              fullWidth
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMergeCategoriesDialogClose}>Cancel</Button>
          <Button onClick={handleMergeCategoriesDialogConfirm} variant="contained" color="primary">
            Merge
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={renameDialogOpen} onClose={handleRenameDialogClose}>
        <DialogTitle>Rename Category</DialogTitle>
        <DialogContent>
          {renameError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {renameError}
          </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="New Name"
            fullWidth
            value={newName}
            onChange={(e) => handleRenameCategoryNameChange(e.target.value)}
            error={!!renameError}
          />
          <TextField
              margin="dense"
              label="Description"
              fullWidth
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
        />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRenameDialogClose}>Cancel</Button>
          <Button onClick={handleRenameDialogConfirm} variant="contained" color="primary">
            Rename
          </Button>
        </DialogActions>
      </Dialog>
      <RenameSubcaseDialog
          open={openRenameSubcaseDialog}
          onClose={handleRenameSubcaseClose}
          onSubmit={handleRenameSubcaseSubmit}
          subcases={activeDocument?.subcases || []}
        />
      <DuplicateCategoryAsDocumentDialog
          open={duplicateCategoryDialogOpen}
          onClose={() => setDuplicateCategoryDialogOpen(false)}
          onConfirm={async (newName, selectedCategories, selectedSubcases) => {
            setLoading(true);
            try {
              // 1. Create the new document
              const newDocument = await createDocument(newName);
              if (!newDocument || !newDocument.categoryId) throw new Error('Failed to create document');

              // 2. Create a map of selected category IDs for quick lookup
              const selectedCategoryIds = new Set(selectedCategories.map(cat => cat.id));
              
              // 3. Helper function to traverse the original tree and create selected categories
              const createSelectedCategories = async (category: Category, parentId: string): Promise<void> => {
                // Skip if this category wasn't selected
                if (!selectedCategoryIds.has(category.id)) {
                  return;
                }
                
                // Skip if this is the root category (same name as document)
                if (category.name === newDocument.name) {
                  return;
                }
                
                // Create the category
                const response = await api.post('/categories', {
                  parentId,
                  name: category.name,
                  description: category.description || ''
                });
                const newCategory = response.data;
                
                // If this category has contents, create them for selected subcases
                if (category.contentCategory && Array.isArray(category.contents)) {
                  for (const content of category.contents) {
                    if (!selectedSubcases.includes(content.subcase)) continue;
                    await api.post('/content', {
                      parentId: newCategory.id,
                      text: content.text,
                      subcase: content.subcase,
                      id: ''
                    });
                  }
                }
                
                // Recursively process children
                if (Array.isArray(category.children)) {
                  for (const child of category.children) {
                    await createSelectedCategories(child, newCategory.id);
                  }
                }
              };
              
              // 4. Start the process from the selected category (the one that was right-clicked)
              if (selectedCategory) {
                // Don't create the selectedCategory itself (it's the root), just process its children
                if (Array.isArray(selectedCategory.children)) {
                  for (const child of selectedCategory.children) {
                    await createSelectedCategories(child, newDocument.categoryId);
                  }
                }
              }

              // 5. Load subcases for the new document
              if (newDocument.categoryId) {
                const response = await api.get('/documents/subcases', { params: { documentId: activeDocument?.categoryId } });
                newDocument.subcases = response.data;
              }

              setLoading(false);
              setDuplicateCategoryDialogOpen(false);
              // 6. Open the new document in a tab
              await handleOpenDocument(newDocument);
            } catch (error) {
              setLoading(false);
              const err = error as Error;
              alert('Failed to duplicate as document: ' + (err.message || error));
            }
          }}
          selectedCategory={selectedCategory}
          categories={categories}
          existingDocumentNames={[...tabs.map(tab => tab.label), ...documents.map(doc => doc.name)]}
          documentId={activeDocument?.id || ''}
          subcaseGroups={subcaseGroups}
          documentSubcases={activeDocument?.subcases || []}
        />
     
      <Dialog open={openDeleteCategoryDialog} onClose={() => setOpenDeleteCategoryDialog(false)}>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the category {selectedCategories.length === 0 ? selectedCategory?.name : selectedCategories[0].name} and all its content?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteCategoryDialog(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              setOpenDeleteCategoryDialog(false);
              await handleDeleteSelectedCategory();
            }}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openDeleteContentDialog} onClose={() => setOpenDeleteContentDialog(false)}>
        <DialogTitle>Delete Content</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedContents.length} content item(s)?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteContentDialog(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              setOpenDeleteContentDialog(false);
              await handleDeleteContent();
            }}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showSubTreeDialogOpen} onClose={() => setShowSubTreeDialogOpen(false)}>
        <DialogTitle>Show Sub Tree</DialogTitle>
        <DialogContent>
          {/* Subcase selector placeholder */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Select Subcases</Typography>
            {loadingSubcaseGroups ? (
              <Box sx={{ my: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2">Loading groups...</Typography>
              </Box>
            ) : subcaseGroups.length > 0 && (
              <Box sx={{ my: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {subcaseGroups.map(group => (
                  <Chip
                    key={group.name}
                    label={group.name}
                    color={selectedSubcaseGroup === group.name ? 'primary' : 'default'}
                    onClick={() => handleSubcaseGroupSelect(group.name)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedSubcases.length === (activeDocument?.subcases?.length || 0)}
                  indeterminate={
                    selectedSubcases.length > 0 &&
                    selectedSubcases.length < (activeDocument?.subcases?.length || 0)
                  }
                  onChange={e => {
                    if (e.target.checked) {
                      setSelectedSubcases(activeDocument?.subcases || []);
                    } else {
                      setSelectedSubcases([]);
                    }
                  }}
                />
              }
              label="Select All"
            />
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {(activeDocument?.subcases || []).map((subcase: string) => (
                <ListItem key={subcase} disablePadding>
                  <ListItemButton onClick={() => {
                    setSelectedSubcases(prev =>
                      prev.includes(subcase)
                        ? prev.filter(s => s !== subcase)
                        : [...prev, subcase]
                    );
                  }}>
                    <Checkbox
                      edge="start"
                      checked={selectedSubcases.includes(subcase)}
                      tabIndex={-1}
                      disableRipple
                    />
                    <ListItemText primary={subcase} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSubTreeDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setSubTreeOverlayData({
                category: selectedCategory,
                subcases: selectedSubcases,
                includeDescriptions,
              });
              setShowSubTreeDialogOpen(false);
              setSubTreeOverlayOpen(true);
            }}
            //disabled={selectedSubcases.length === 0}
            variant="contained"
            color="primary"
          >
            Show
          </Button>
        </DialogActions>
      </Dialog>
      <SubcaseGroupsDialog
        open={subcaseGroupsDialogOpen}
        onClose={() => setSubcaseGroupsDialogOpen(false)}
        documentId={activeDocument?.categoryId || ''}
        documentSubcases={activeDocument?.subcases || []}
      />
      
      <FindCategoryDialog
        open={findDialogOpen}
        onClose={handleFindDialogClose}
        categories={categories}
        selectedCategory={selectedCategory}
        onCategorySelect={setSelectedCategory}
        onExpandParents={expandParentsToMakeVisible}
        findCategoryInTree={findCategoryInTree}
        subcaseGroups={subcaseGroups}
        documentSubcases={activeDocument?.subcases || []}
        documentName={activeDocument?.name}
        onContentSelect={(categoryId: string, contentId: string, searchString?: string, searchTerms?: string[], searchMode?: 'any' | 'all' | 'exact' | 'semantic', wholeWord?: boolean, caseSensitive?: boolean, allMatchingContentIds?: string[]) => {
          
          // Find the content and select it
          const category = findCategoryInTree(categories, categoryId);
          if (category && category.contents) {
            const content = category.contents.find(c => c.id === contentId);
            if (content) {
              setSelectedContents([content]);
              setCurrentContent(content);
              // Use the passed searchString if available, otherwise use the current findSearchString
              const textToHighlight = searchString || findSearchString;
              setHighlightedText(textToHighlight);
              // Store search terms and options for highlighting
              setSearchTerms(searchTerms || []);
              setSearchMode(searchMode || 'exact');
              setWholeWord(wholeWord || false);
              setCaseSensitive(caseSensitive || false);
              setMatchingContentIds(allMatchingContentIds || [contentId]);
              
              // Auto-scroll to the content pane
              setTimeout(() => {
                if (contentPaneRef.current) {
                  contentPaneRef.current.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start' 
                  });
                }
              }, 10); // Small delay to ensure content is rendered
              
              // Auto-scroll to the specific content row in CategoryContent
              setTimeout(() => {
                scrollToContent(contentId);
              }, 200); // Slightly longer delay to ensure content is rendered and selected
            }
          }
        }}
      />
      <GenerateSubtreeDialog
          open={generateSubtreeDialogOpen}
          onClose={handleGenerateSubtreeDialogClose}
          category={selectedCategory}
          onSuccess={handleGenerateSubtreeSuccess}
        />
      <HelpDialog
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
      />
      <UserProfileDialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
      />
    </Box>
  );
};

export default MainLayout; 